// ==========================================
// --- SECTION 1: IMPORTS & DEPENDENCIES ---
// ==========================================
import React, { useState, useEffect, useRef } from "react";
import VariantesPanel from './components/wizards/VariantesPanel';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  SignOutButton,
  useUser,
  useSession,
  useClerk,
} from "@clerk/clerk-react";
import LandingPage from "./components/wizards/LandingPage";
import {
  Layout,
  Code,
  Database,
  Zap,
  RefreshCw,
  Ship,
  GitBranch,
  FolderOpen,
  Smartphone,
  Bug,
  CheckCircle,
  ShieldCheck,
  FileText,
  Braces,
  Languages,
  Search,
  Settings2,
  Sparkles,
  ArrowLeft,
  Terminal,
  Copy,
  Check,
  MessageSquare,
  Bot,
  Sparkle,
  Trash2,
  LogOut,
  Menu,
  X,
  MessageCircle,
  Eye,
  Download,
  Star,
  Info,
  ChevronRight,
  Mic,
  BookOpen,
  Lock,
  Cpu,
  Activity,
  ArrowUp,
  Plus,
  ClipboardCheck,
  Shield,
  Users,
  Clock,
  Globe,
  Layers,
  Code2,
  Cloud,
} from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import DOMPurify from 'dompurify';
// --- WIZARD COMPONENTS ---
import SqlWizard from "./components/wizards/SqlWizard";
import ProgramZWizard from "./components/wizards/ProgramZWizard";
import ModernizationWizard from "./components/wizards/ModernizationWizard";
import S4MigrationWizard from "./components/wizards/S4MigrationWizard";
import CdsWizard from "./components/wizards/CdsWizard";
import CdsCompositeWizard from './components/wizards/CdsCompositeWizard';
import FioriWizard from "./components/wizards/FioriWizard";
import AbapUnitWizard from "./components/wizards/AbapUnitWizard";
import ApiMappingWizard from "./components/wizards/ApiMappingWizard";
import PerformanceWizard from "./components/wizards/PerformanceWizard";
import BusinessFlowWizard from "./components/wizards/BusinessFlowWizard";
import ST22Wizard from "./components/wizards/ST22Wizard";
import SecurityWizard from "./components/wizards/SecurityWizard";
import AlvWizard from "./components/wizards/AlvWizard";
import TechnicalBlueprintWizard from "./components/wizards/TechnicalBlueprintWizard";
import TranslationWizard from "./components/wizards/TranslationWizard";
import DocumentWizard from "./components/wizards/DocumentWizard";
import SpecValidatorWizard from "./components/wizards/SpecValidatorWizard";
import UserGuideViewer from "./components/wizards/UserGuideViewer";
import mermaid from 'mermaid';
import RapWizard from "./components/wizards/RapWizard";
import RapExplorer from "./components/wizards/RapExplorer";
import CapWizard from "./components/wizards/CapWizard";
import CapExplorer from "./components/wizards/CapExplorer";

// --- END SECTION 1 ---
// ============================================================
// URLs existentes - cambiar solo la parte del dominio según el entorno (local o producción)
// ─── CONFIGURACIÓN DE ENTORNO ─────────────────────────────────────────────────
const IS_LOCAL = false; // ← cambiá solo este valor: true=local, false=producción
const BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001";
const API_URL = `${BASE}/api/generate`;
const FORO_URL = `${BASE}/api/foro`;
const USUARIOS_URL = `${BASE}/api/usuarios`;
const PAYPAL_URL = `${BASE}/api/paypal`;
const MP_URL = `${BASE}/api/mp`;
const VARIANTES_URL = `${BASE}/api/variantes`;
// ==========================================
// --- SECTION 2: CONSTANTS & CONFIG ---
// ==========================================
const myBooks = [
  {
    id: 1,
    title: "ABAP Nueva Generación",
    description:
      "Modernización hacia SAP Cloud, BTP e IA Generativa. El mapa estratégico para liderar el nuevo ecosistema SAP.",
    price: "15.790,00",
    image: "/images/libros/portada-abap-gen.jpg",
    link: "https://go.hotmart.com/Q105106501C",
  },
  {
    id: 2,
    title: "Desarrollo de Aplicación CAP",
    description:
      "Camino directo para dominar SAP Cloud Application Programming Model: desde entorno hasta OData funcional.",
    price: "11.049,00",
    image: "/images/libros/portada-desa-cap-1.jpg",
    link: "https://go.hotmart.com/V105017119H",
  },
  {
    id: 3,
    title: "Fiori Elements sobre CAP - Vol 2",
    description:
      "Guía técnica paso a paso para construir apps Fiori Elements conectadas a CAP y desplegadas en SAP BTP.",
    price: "14.700,00",
    image: "/images/libros/portada-fiori-cap-2.jpg",
    link: "https://go.hotmart.com/I105024650E",
  },
  {
    id: 4,
    title: "De Cero a Consultor SAP 2026",
    description:
      "La ruta real para no perder meses estudiando sin rumbo. Información de alto valor para perfiles con proyección.",
    price: "6.307,00",
    image: "/images/libros/portada-cero-consultor-sap.png",
    link: "https://go.hotmart.com/I104981164J?dp=1",
  },
  {
    id: 5,
    title: "Entorno de Desarrollo SAPUI5",
    description:
      "Configuración completa usando SAP Business Application Studio (BAS). Puesta en marcha oficial en la nube.",
    price: "6.307,00",
    image: "/images/libros/portada-sap-ui.png",
    link: "https://go.hotmart.com/U104980433A?dp=1",
  },
  {
    id: 6,
    title: "Habilitación Campo EBELN en FBL1N",
    description:
      "Solución técnica para visualizar el Documento de Compras en partidas individuales. Incluye 2 documentos.",
    price: "6.307,00",
    image: "/images/libros/portada-campo-ebeln-fbl1n.jpg",
    link: "https://go.hotmart.com/V104981588K?dp=1",
  },
  {
    id: 7,
    title: "SAP + IA: Evolución Estratégica",
    description:
      "Plan concreto para el perfil híbrido 2026/2030 para consultores que buscan evolucionar hacia el perfil híbrido que exige el mercado.",
    price: "7.904,00",
    image: "/images/libros/portada-sap-+-ia.jpeg",
    link: "https://go.hotmart.com/E104980958I?dp=1",
  },
];

const tools = [
  {
    id: "alv",
    title: "SAP - REPORTES ALV",
    icon: <Layout />,
    tag: "Reporting",
    btn: "Generar Código ALV",
    description: "Genera informes de listas avanzados con SALV o Clásico.",
  },
  {
    id: "new_prog",
    title: "SAP - PROGRAMA Z",
    icon: <Code />,
    tag: "Development",
    btn: "Generar Arquitectura Z",
    description: "Crea la estructura base de un programa ejecutable e incluye.",
  },
  {
    id: "sql",
    title: "SAP - OPTIMIZADOR SQL",
    icon: <Database />,
    tag: "Optimization",
    btn: "Optimizar Sentencia SQL",
    description: "Transforma consultas SELECT ineficientes en sentencias HANA.",
  },
  {
    id: "opt",
    title: "SAP - OPTIMIZACIÓN DEL RENDIMIENTO",
    icon: <Zap />,
    tag: "Optimization",
    btn: "Optimizar Código",
    description: "Análisis para implementar Field Symbols o Binary Search.",
  },
  {
    id: "modern",
    title: "SAP - MODERNIZACIÓN DE LA SINTAXIS",
    icon: <RefreshCw />,
    tag: "Development",
    btn: "Refactorizar a 7.40+",
    description: "Actualiza código legacy a declaraciones inline y VALUE #.",
  },
  {
    id: "migrator",
    title: "SAP - S/4HANA MIGRATION",
    icon: <Ship />,
    tag: "Development",
    btn: "Migrar a S/4HANA",
    description: "Identifica tablas obsoletas y sugiere el modelo ACDOCA.",
  },
  {
    id: "flowchart",
    title: "SAP - FLUJO DE NEGOCIO",
    icon: <GitBranch />,
    tag: "Optimization",
    btn: "Diseñar Flujo Lógico",
    description:
      "Convierte requerimientos en una secuencia técnica funcional para ABAP.",
  },
  {
    id: "cds",
    title: "SAP - MODELADOR VISTAS CDS",
    icon: <FolderOpen size={20} />,
    tag: "Development",
    btn: "Crear Vista CDS",
    description: "Diseña vistas CDS con anotaciones UI y Analytics.",
  },
  {
    id: "cds_composite",
    title: "SAP - CDS COMPOSITE VIEW",
    icon: <Layers size={20} />,
    tag: "Development",
    btn: "Crear Vista Composite",
    description: "Une dos CDS existentes en una vista Composite + Consumption. No requiere tablas físicas.",
  },
  {
    id: "fiori",
    title: "SAP - FIORI ELEMENTS UI",
    icon: <Smartphone />,
    tag: "Development",
    btn: "Generar UI Fiori",
    description:
      "Genera anotaciones XML para aplicaciones basadas en metadatos.",
  },
  {
    id: "debug",
    title: "SAP - ST22 RUNTIME ANALYSIS",
    icon: <Bug />,
    tag: "Optimization",
    btn: "Analizar Error (Dump)",
    description: "Analiza Short Dumps para identificar causas y correcciones.",
  },
  {
    id: "test",
    title: "SAP - ABAP UNIT TESTING",
    icon: <CheckCircle />,
    tag: "Development",
    btn: "Generar Unit Tests",
    description: "Crea clases de prueba locales para validación de lógica.",
  },
  {
    id: "security",
    title: "SAP - AUTHORIZATION CHECK",
    icon: <ShieldCheck />,
    tag: "Optimization",
    btn: "Generar Check de Seguridad",
    description: "Implementa AUTHORITY-CHECK y lógica de seguridad ABAP.",
  },
  {
    id: "doc",
    title: "SAP - TECHNICAL BLUEPRINT",
    icon: <FileText />,
    tag: "Reporting",
    btn: "Generar Documentación Técnica",
    description: "Genera documentos de diseño técnico estructurados.",
  },
  {
    id: "json_abap",
    title: "SAP - API DATA MAPPING",
    icon: <Braces />,
    tag: "Development",
    btn: "Mapear JSON a ABAP",
    description: "Convierte JSON en tipos de datos ABAP y deserialización.",
  },
  {
    id: "translator",
    title: "SAP - CODE TRANSLATION",
    icon: <Languages />,
    tag: "Reporting",
    btn: "Traducir Activos Técnicos",
    description:
      "Traduce comentarios, etiquetas y manuales manteniendo integridad.",
  },
  {
    id: "documentos",
    title: "SAP - DOCUMENT GENERATION",
    icon: <FileText />,
    tag: "Reporting",
    btn: "Generar Manual Usuario",
    description: "Redacta manuales de usuario para el cliente final.",
  },
  {
    id: "specValidator",
    title: "SAP - VALIDADOR DE ESPECIFICACIÓN",
    icon: <ClipboardCheck />,
    tag: "Quality",
    btn: "Validar Especificación",
    description:
      "Analiza documentos funcionales y evalúa completitud contra el modelo estándar de 10 secciones.",
  },
  {
    id: "rap",
    title: "SAP - RAP ARCHITECT",
    icon: <Code2 />,
    tag: "Development",
    btn: "Generar Stack RAP",
    description: "RESTful Application Programming — Stack completo con Behavior, CDS y Service Binding.",
  },
  {
    id: "cap",
    title: "SAP - CAP ARCHITECT",
    icon: <Cloud />,
    tag: "Development",
    btn: "Generar Stack CAP",
    description: "Cloud Application Programming — Node.js/Java con deploy en SAP BTP.",
  },
];

const initialWizardData = {
  fioriPlatform: "ABAP", // ABAP | CAP
  docTitle: "",
  docModule: "FI-CO",
  docType: "Full (Tech + User)",
  docOptions: {
    techSpecs: false,
    userSteps: false,
    tablesUsed: false,
    errorGuide: false,
  },
  bpTitle: "",
  bpModules: "",
  bpOptions: {
    dataDict: false,
    classDiagram: false,
    testPlan: false,
    estimations: false,
  },
  alvTable: "",
  alvType: "SALV",
  alvOptions: {
    zebra: false,
    toolbar: false,
    optimize: false,
    editable: false,
  },
  authObject: "",
  authActivity: "",
  securityOpts: {
    encapsulate: false,
    handleError: false,
    addLogging: false,
    multiLevel: false,
  },
  dumpErrorId: "",
  dumpProgram: "",
  dumpOptions: {
    explainCause: false,
    suggestFix: false,
    preventiveMeasures: false,
    technicalNote: false,
  },
  flowName: "",
  flowType: "Custom",
  flowOptions: {
    includeErrors: false,
    includePseudoCode: false,
    diagramType: false,
    modularization: false,
  },
  sqlTargetDB: "HANA",
  sqlTables: "",
  sqlStrategy: "Performance",
  sqlHanaOpts: { inline: false, faeToJoin: false, strict: false },
  techBlueprint: "",
  techReqs: "",
  techAudience: "Development Team",
  techOpts: {
    includeDiagrams: false,
    includeCodeSnippets: false,
    includeTimeline: false,
    includeRisks: false,
  },
  progName: "",
  progDesc: "",
  progAuthor: "",
  progOT: "",
  progIncludes: ["Z_PROJ_TOP", "Z_PROJ_SEL", "Z_PROJ_F01"],
  progPerforms: ["OBTENER_DATOS", "PROCESAR_LOGICA", "MOSTRAR_RESULTADOS"],
  progEvents: { init: false, start: false },
  progOverrideManual: false,
  modernOptions: {
    inline: false,
    constructors: false,
    tableExp: false,
    strings: false,
  },
  migrationOpts: { acdoca: false, matdoc: false, bp: false, matnr: false },
  modernization: {
    inline: false,
    constructors: false,
    tableExp: false,
    strings: false,
  },
  cdsName: "",
  sqlViewName: "",
  cdsCategory: "#FACT",
  cdsOptions: { odata: false, analytics: false },
  fioriService: "",
  fioriTemplate: "List Report",
  fioriOptions: { search: false, crud: false, draft: false, excel: false },
  unitTarget: "",
  unitRisk: "HARMLESS",
  unitDuration: "SHORT",
  unitOptions: { setup: false, teardown: false },
  apiStructName: "",
  apiMethod: "DESERIALIZE",
  apiOptions: { prettyName: false, skipEmpty: false, genLogic: false },
  perfBottleneck: "ABAP_LOGIC",
  perfVolume: "MEDIUM",
  perfOpts: {
    fieldSymbols: false,
    binarySearch: false,
    noNested: false,
    parallel: false,
  },
  fileContext: null,
  fileStatus: "pending",
  alvTitle: "",
  alvWhereClause: "",
  alvHotspotFields: "",
  alvHotspotList: [],
  progGenDDIC: false,
  progTables: [],
  abapVersion: "S4HANA", // S4HANA | ABAP750 | ABAP740

  // RAP Wizard
  keyFields: [{ name: "Id", type: "abap.uuid", isKey: true }],
  dataFields: [{ name: "", type: "abap.char(40)" }],
  draftViewName: "",
  operations: { create: true, update: true, delete: true, read: true },
  associations: [],
  actions: [],
  validations: [],
  determinations: [],
  ziName: "",
  zcName: "",
  sqlViewZI: "",
  sqlViewZC: "",
  entityName: "",
  sourceTable: "",
  template: "managed",
  draftEnabled: false,
  serviceDefName: "",
  serviceBindingName: "",
  bindingType: "v4",
  exposeAssociations: true,
  zTableName: "",
  zDraftTableName: "",

  // CAP Wizard
  capProjectName: "",
  capRuntime: "nodejs",
  capDatabase: "sqlite",
  capNamespace: "",
  capEntities: [
    {
      name: "",
      fields: [
        { name: "ID", type: "UUID", isKey: true, notNull: true },
        { name: "", type: "String", isKey: false, notNull: false },
      ],
    },
  ],
  capAssociations: [],
  capServiceName: "",
  capServiceEntities: [],
  capFioriAnnotations: true,
  capODataVersion: "v4",
  capAuthEnabled: false,
  capMtaEnabled: false,
  capAppRouterEnabled: false,
  capXsuaaEnabled: false,
  capDestinationEnabled: false,
  capHdiEnabled: false,

};

// ==========================================
// --- PARTE 1: LOS TEXTOS DEL MANUAL ---
// ==========================================
const TOOL_MANUALS = {
  alv: {
    purpose: "Generar reportes de listas avanzados con navegación dinámica.",
    steps: [
      "Define la tabla base (ej. EKKO).",
      "Agrega Hotspots (campos clicables).",
      "Selecciona el estilo (SALV o Grid).",
    ],
    result:
      "Código ABAP completo con manejo de eventos y optimización de columnas.",
  },
  new_prog: {
    purpose: "Crear esqueletos de programas Z siguiendo Clean ABAP.",
    steps: [
      "Indica nombre y descripción.",
      "Selecciona Includes (TOP, SEL, F01).",
      "Define parámetros de pantalla.",
    ],
    result:
      "Reporte ejecutable con modularización profesional y eventos estándar.",
  },
};

// ==========================================
// --- PARTE 2: EL COMPONENTE DEL MODAL ---
// ==========================================
const ManualModal = ({ tool, isOpen, onClose, onStart }) => {
  if (!isOpen || !tool) return null;

  const manual = TOOL_MANUALS[tool.id] || {
    purpose: tool.description,
    steps: [
      "Completa los campos del Wizard.",
      "Adjunta contexto si es necesario.",
      "Haz clic en Generar.",
    ],
    result: "Código optimizado listo para SAP.",
  };

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-6 backdrop-blur-md bg-black/60 animate-in fade-in duration-300">
      <div className="bg-[#0B0F1A] border border-blue-500/30 w-full max-w-2xl rounded-[40px] p-10 shadow-[0_0_50px_rgba(37,99,235,0.2)] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="flex justify-between items-start mb-8 relative z-10 text-left">
          <div className="flex items-center gap-4">
            <div className="bg-blue-600 p-3 rounded-2xl text-white shadow-lg shadow-blue-500/20">
              {tool.icon}
            </div>
            <div className="text-left">
              <h3 className="text-xl font-black uppercase italic tracking-tighter text-white">
                Manual de Usuario
              </h3>
              <p className="text-blue-400 text-[10px] font-black uppercase tracking-[0.3em]">
                {tool.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="space-y-8 relative z-10 text-left">
          <div>
            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Sparkle size={12} className="text-blue-500" /> Propósito del
              Módulo
            </h4>
            <p className="text-slate-300 text-sm leading-relaxed font-medium">
              {manual.purpose}
            </p>
          </div>
          <div>
            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <ChevronRight size={12} className="text-blue-500" /> Guía Paso a
              Paso
            </h4>
            <div className="grid gap-3">
              {manual.steps.map((step, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5 hover:border-blue-500/30 transition-all group"
                >
                  <span className="text-blue-500 font-black italic text-lg opacity-50 group-hover:opacity-100 transition-opacity">
                    {i + 1}
                  </span>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-blue-600/5 border border-blue-500/20 p-6 rounded-3xl">
            <h4 className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-2">
              Resultado Esperado
            </h4>
            <p className="text-[11px] text-slate-400 font-medium italic">
              "{manual.result}"
            </p>
          </div>
        </div>
        <button
          onClick={onStart}
          className="w-full mt-10 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] transition-all shadow-xl shadow-blue-600/20 active:scale-95"
        >
          Entendido, Comenzar
        </button>
      </div>
    </div>
  );
};
// --- END SECTION 2 ---
const ForoComunitario = ({ user, selectedTool, onClose }) => {
  const { session } = useSession();
  const [mensajes, setMensajes] = React.useState([]);
  const [nuevoMensaje, setNuevoMensaje] = React.useState("");
  const [enviando, setEnviando] = React.useState(false);
  const [error, setError] = React.useState("");
  const [respondiendo, setRespondiendo] = React.useState(null); // id del mensaje que se está respondiendo
  const [textoRespuesta, setTextoRespuesta] = React.useState("");
  const [enviandoRespuesta, setEnviandoRespuesta] = React.useState(false);
  const [cargando, setCargando] = React.useState(true);

  // Carga inicial de mensajes
  React.useEffect(() => {
    cargarMensajes();
    // Refresca cada 30 segundos para ver mensajes nuevos
    const interval = setInterval(cargarMensajes, 30000);
    return () => clearInterval(interval);
  }, []);

  // Función para eliminar un mensaje (solo si el usuario es el autor)
  const eliminarMensaje = async (mensajeId) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;

    try {
      // 1. Obtenemos el token de seguridad de la sesión activa
      const token = await session.getToken();

      // 2. Realizamos la petición con el header de Authorization
      const res = await fetch(`${FORO_URL}/eliminar/${mensajeId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` // <--- LA LLAVE DE SEGURIDAD
        },
        body: JSON.stringify({}), // Ya no enviamos el email aquí, el token ya lo dice
      });

      const data = await res.json();
      if (data.success) {
        await cargarMensajes(); // Refrescar foro
      } else {
        alert(data.error || "No tienes permiso para eliminar este mensaje");
      }
    } catch (e) {
      console.error("Error eliminando:", e);
    }
  };

  // Función para cargar mensajes desde el backend
  const cargarMensajes = async () => {
    try {
      const res = await fetch(FORO_URL);
      const data = await res.json();
      setMensajes(data.mensajes || []);
    } catch (e) {
      console.error("Error cargando foro:", e);
    } finally {
      setCargando(false);
    }
  };

  const publicarMensaje = async () => {
    if (!nuevoMensaje.trim() || enviando) return;
    setEnviando(true);
    setError("");
    try {
      const res = await fetch(FORO_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mensaje: nuevoMensaje,
          usuario: user?.fullName || "Consultor SAP",
          email: user?.primaryEmailAddress?.emailAddress,
          herramienta: selectedTool?.title || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setNuevoMensaje("");
        await cargarMensajes();
      } else if (data.error === "MODERACION") {
        setError(`⚠️ Mensaje rechazado: ${data.motivo}`);
      } else {
        setError(data.error || "Error al publicar");
      }
    } catch (e) {
      setError("Error de conexión");
    } finally {
      setEnviando(false);
    }
  };

  const publicarRespuesta = async (mensajeId) => {
    if (!textoRespuesta.trim() || enviandoRespuesta) return;
    setEnviandoRespuesta(true);
    setError("");
    try {
      const res = await fetch(`${FORO_URL}/responder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mensajeId,
          respuesta: textoRespuesta,
          usuario: user?.fullName || "Consultor SAP",
          email: user?.primaryEmailAddress?.emailAddress,
          herramienta: selectedTool?.title || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTextoRespuesta("");
        setRespondiendo(null);
        await cargarMensajes();
      } else if (data.error === "MODERACION") {
        setError(`⚠️ Respuesta rechazada: ${data.motivo}`);
      } else {
        setError(data.error || "Error al responder");
      }
    } catch (e) {
      setError("Error de conexión");
    } finally {
      setEnviandoRespuesta(false);
    }
  };

  const formatearFecha = (iso) => {
    const fecha = new Date(iso);
    return fecha.toLocaleDateString("es-AR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
    });
  };

  const iniciales = (nombre) => {
    return nombre?.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase() || "??";
  };

  return (
    <div className="max-w-4xl mx-auto text-left">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-blue-600">
            <MessageCircle size={14} />
            <span className="text-[9px] font-black uppercase tracking-[0.3em]">Comunidad ORQUEST</span>
          </div>
          <button
            onClick={() => onClose()}
            className="flex items-center gap-2 text-slate-400 font-black text-[9px] uppercase hover:text-blue-600 transition-all border border-white/5 px-4 py-2 rounded-full tracking-widest"
          >
            <ArrowLeft size={14} /> Workspace Central
          </button>
        </div>
      </div>
      {/* Input nuevo mensaje */}
      <div className="bg-white/5 border border-white/10 rounded-[28px] p-6 mb-6">
        <p className="text-[9px] font-black text-white/30 uppercase tracking-widest mb-3">
          Compartí con la comunidad
        </p>
        <textarea
          className="w-full h-24 p-4 bg-black/40 border border-white/10 rounded-2xl focus:border-blue-500/50 outline-none text-sm text-white placeholder:text-slate-700 resize-none font-mono"
          placeholder="Preguntá, compartí código, tips de ABAP, errores resueltos..."
          value={nuevoMensaje}
          onChange={(e) => { setNuevoMensaje(e.target.value); setError(""); }}
          maxLength={500}
        />
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-3">
            {error && (
              <p className="text-[10px] text-red-400 font-bold">{error}</p>
            )}
            {selectedTool && (
              <span className="text-[9px] font-black px-2 py-1 rounded-lg bg-blue-600/15 border border-blue-500/20 text-blue-400 uppercase tracking-wide">
                📌 {selectedTool.title}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[9px] text-white/20">{nuevoMensaje.length}/500</span>
            <button
              onClick={publicarMensaje}
              disabled={!nuevoMensaje.trim() || enviando}
              className={`px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all
                ${!nuevoMensaje.trim() || enviando
                  ? "bg-white/5 text-white/20 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-500 text-white shadow-lg active:scale-95"
                }`}
            >
              {enviando ? (
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Moderando...
                </span>
              ) : "Publicar"}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de mensajes */}
      {cargando ? (
        <div className="flex items-center justify-center py-20">
          <span className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : mensajes.length === 0 ? (
        <div className="text-center py-20">
          <MessageCircle size={40} className="text-white/10 mx-auto mb-4" />
          <p className="text-[11px] font-black text-white/20 uppercase tracking-widest">
            Sé el primero en publicar
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {mensajes.map((msg) => (
            <div key={msg.id} className="bg-white/5 border border-white/8 rounded-[24px] p-5">
              {/* Cabecera del mensaje */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                  <span className="text-[11px] font-black text-blue-400">{iniciales(msg.usuario)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {msg.email === user?.primaryEmailAddress?.emailAddress && (
                      <button
                        onClick={() => eliminarMensaje(msg.id)}
                        className="text-[9px] text-white/20 hover:text-red-400 transition-colors ml-auto"
                        title="Eliminar mensaje"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                    <span className="text-[11px] font-black text-white">{msg.usuario}</span>
                    {msg.herramienta && (
                      <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-blue-600/10 border border-blue-500/20 text-blue-400 uppercase">
                        {msg.herramienta}
                      </span>
                    )}
                    <span className="text-[9px] text-white/20">{formatearFecha(msg.fecha)}</span>
                  </div>
                  <p className="text-[12px] text-slate-300 mt-2 leading-relaxed">{msg.mensaje}</p>
                </div>
              </div>

              {/* Respuestas anidadas */}
              {msg.respuestas?.length > 0 && (
                <div className="ml-12 space-y-3 mb-3">
                  {msg.respuestas.map((resp) => (
                    <div key={resp.id} className="bg-black/30 border border-white/5 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <div className="w-6 h-6 rounded-lg bg-emerald-600/20 border border-emerald-500/20 flex items-center justify-center">
                          <span className="text-[8px] font-black text-emerald-400">{iniciales(resp.usuario)}</span>
                        </div>
                        <span className="text-[10px] font-black text-white">{resp.usuario}</span>
                        {resp.herramienta && (
                          <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-emerald-600/10 border border-emerald-500/20 text-emerald-400 uppercase">
                            {resp.herramienta}
                          </span>
                        )}
                        <span className="text-[9px] text-white/20">{formatearFecha(resp.fecha)}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 leading-relaxed ml-8">{resp.mensaje}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Botón responder / input de respuesta */}
              {respondiendo === msg.id ? (
                <div className="ml-12 mt-3">
                  <textarea
                    className="w-full h-16 p-3 bg-black/40 border border-white/10 rounded-xl focus:border-emerald-500/50 outline-none text-[12px] text-white placeholder:text-slate-700 resize-none"
                    placeholder="Escribí tu respuesta..."
                    value={textoRespuesta}
                    onChange={(e) => setTextoRespuesta(e.target.value)}
                    maxLength={500}
                    autoFocus
                  />
                  <div className="flex items-center gap-2 mt-2 justify-end">
                    <button
                      onClick={() => { setRespondiendo(null); setTextoRespuesta(""); setError(""); }}
                      className="text-[9px] font-black text-white/30 hover:text-white uppercase tracking-widest px-3 py-1.5"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => publicarRespuesta(msg.id)}
                      disabled={!textoRespuesta.trim() || enviandoRespuesta}
                      className={`px-4 py-1.5 rounded-lg font-black text-[9px] uppercase tracking-widest transition-all
                        ${!textoRespuesta.trim() || enviandoRespuesta
                          ? "bg-white/5 text-white/20 cursor-not-allowed"
                          : "bg-emerald-600 hover:bg-emerald-500 text-white active:scale-95"
                        }`}
                    >
                      {enviandoRespuesta ? "Moderando..." : "Responder"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setRespondiendo(msg.id); setTextoRespuesta(""); setError(""); }}
                  className="ml-12 mt-1 text-[9px] font-black text-white/25 hover:text-blue-400 uppercase tracking-widest transition-colors flex items-center gap-1"
                >
                  <ArrowLeft size={10} className="rotate-180" /> Responder
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// PayModal COMPLETO — Reemplazar todo el componente actual
// Pegar ANTES de function App()
// ============================================================

const PayModal = ({ isPayModalOpen, setIsPayModalOpen, payLoading, pagarConPayPal, pagarConMP, user }) => {
  const [emailsAdicionales, setEmailsAdicionales] = React.useState([]);
  const [errorEmail, setErrorEmail] = React.useState("");
  const inputRef = React.useRef(null);

  if (!isPayModalOpen) return null;

  // Calcula precio según cantidad de emails
  const calcularPrecio = (cantidad) => {
    if (cantidad === 1) return 20;
    if (cantidad === 2) return 37;
    if (cantidad === 3) return 55;
    return cantidad * 15;
  };

  const totalEmails = 1 + emailsAdicionales.length;
  const precio = calcularPrecio(totalEmails);

  const agregarEmail = () => {
    const val = inputRef.current?.value?.trim().toLowerCase();
    if (!val) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setErrorEmail("Email inválido");
      return;
    }
    if (val === user?.primaryEmailAddress?.emailAddress?.toLowerCase()) {
      setErrorEmail("Ese es tu propio email, ya está incluido");
      return;
    }
    if (emailsAdicionales.includes(val)) {
      setErrorEmail("Este email ya está en la lista");
      return;
    }
    if (emailsAdicionales.length >= 9) {
      setErrorEmail("Máximo 10 emails en total");
      return;
    }
    setEmailsAdicionales(prev => [...prev, val]);
    setErrorEmail("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const quitarEmail = (email) => {
    setEmailsAdicionales(prev => prev.filter(e => e !== email));
  };

  const handleCerrar = () => {
    setIsPayModalOpen(false);
    setEmailsAdicionales([]);
    setErrorEmail("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-4 backdrop-blur-sm bg-black/70">
      <div className="bg-[#0B0F1A] border border-white/10 w-full max-w-md rounded-[32px] shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="relative p-6 border-b border-white/5 sticky top-0 bg-[#0B0F1A] z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/8 to-transparent" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Zap size={18} className="text-white" />
              </div>
              <div>
                <h3 className="text-[14px] font-black uppercase tracking-widest text-white leading-none">
                  Activar Plan PRO
                </h3>
                <p className="text-[10px] text-blue-400 font-black uppercase tracking-wider mt-0.5">
                  USD {precio} · {totalEmails} usuario{totalEmails > 1 ? "s" : ""}
                </p>
              </div>
            </div>
            <button
              onClick={handleCerrar}
              className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="px-6 pt-5 pb-6">

          {/* Beneficios */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            {["17 herramientas IA", "Generaciones ilimitadas", "Soporte prioritario", "Actualizaciones incluidas"].map((b) => (
              <div key={b} className="flex items-center gap-2 bg-white/3 border border-white/5 rounded-xl px-3 py-2">
                <CheckCircle size={11} className="text-emerald-400 shrink-0" />
                <span className="text-[10px] text-white/60 font-black uppercase tracking-wide">{b}</span>
              </div>
            ))}
          </div>

          {/* Tu email (incluido siempre) */}
          <div className="mb-4">
            <p className="text-[9px] text-white/25 uppercase tracking-[0.15em] mb-2">Tu cuenta</p>
            <div className="flex items-center gap-2 bg-blue-600/10 border border-blue-500/20 rounded-xl px-3 py-2.5">
              <CheckCircle size={12} className="text-blue-400 shrink-0" />
              <span className="text-[11px] text-white font-bold flex-1 truncate">
                {user?.primaryEmailAddress?.emailAddress}
              </span>
              <span className="text-[9px] text-blue-400 font-black">INCLUIDO</span>
            </div>
          </div>

          {/* Agregar emails adicionales */}
          <div className="mb-4">
            <p className="text-[9px] text-white/25 uppercase tracking-[0.15em] mb-2">
              Activar PRO para colegas (opcional)
            </p>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="email"
                className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[11px] text-white placeholder:text-white/20 outline-none focus:border-blue-500/50"
                placeholder="email@colega.com"
                onKeyDown={(e) => { if (e.key === "Enter") agregarEmail(); }}
                onChange={() => setErrorEmail("")}
              />
              <button
                onClick={agregarEmail}
                className="px-3 py-2.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white transition-all"
              >
                <Plus size={14} />
              </button>
            </div>
            {errorEmail && (
              <p className="text-[10px] text-red-400 mt-1">{errorEmail}</p>
            )}

            {/* Lista de emails adicionales */}
            {emailsAdicionales.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {emailsAdicionales.map((email) => (
                  <div key={email} className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-xl px-3 py-2">
                    <CheckCircle size={11} className="text-emerald-400 shrink-0" />
                    <span className="text-[11px] text-white/70 flex-1 truncate">{email}</span>
                    <button onClick={() => quitarEmail(email)} className="text-white/20 hover:text-red-400 transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resumen de precio */}
          <div className="bg-white/3 border border-white/8 rounded-2xl px-4 py-3 mb-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-white/40 uppercase tracking-wide">
                {totalEmails} usuario{totalEmails > 1 ? "s" : ""}
              </span>
              <span className="text-[14px] font-black text-white">USD {precio}</span>
            </div>
            {totalEmails === 2 && <p className="text-[9px] text-emerald-400 font-black">💚 Ahorrás USD 3 vs precio individual</p>}
            {totalEmails === 3 && <p className="text-[9px] text-emerald-400 font-black">💚 Ahorrás USD 5 vs precio individual</p>}
            {totalEmails >= 4 && <p className="text-[9px] text-emerald-400 font-black">💚 USD 15 por usuario (precio especial)</p>}
          </div>

          <p className="text-[9px] text-white/25 uppercase tracking-[0.15em] mb-3 text-center">
            Método de pago
          </p>
          {/* Botón Mercado Pago */}
          <button
            onClick={pagarConMP}
            disabled={payLoading !== null}
            className="w-full mb-3 flex items-center justify-between px-5 py-4 rounded-2xl bg-[#00BCFF]/10 border border-[#00BCFF]/30 hover:bg-[#00BCFF]/20 hover:border-[#00BCFF]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              {payLoading === 'mp' ? (
                <span className="w-5 h-5 border-2 border-[#00BCFF]/30 border-t-[#00BCFF] rounded-full animate-spin" />
              ) : (
                <div className="w-8 h-8 bg-[#00BCFF] rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-[10px]">MP</span>
                </div>
              )}
              <div className="text-left">
                <p className="text-[12px] font-black text-white uppercase tracking-wide leading-none">Mercado Pago</p>
                <p className="text-[9px] text-white/40 mt-0.5">Tarjeta argentina · Sin cuenta requerida</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black text-[#00BCFF]">ARS</p>
              <p className="text-[8px] text-white/25">Cotización oficial</p>
            </div>
          </button>

          {/* Separador */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-white/5" />
            <span className="text-[9px] text-white/20 uppercase tracking-widest">o pagá en USD</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>
          {/* Botón PayPal */}
          <button
            onClick={() => pagarConPayPal(emailsAdicionales)}
            disabled={payLoading !== null}
            className="w-full mb-5 flex items-center justify-between px-5 py-4 rounded-2xl bg-[#0070ba]/10 border border-[#0070ba]/30 hover:bg-[#0070ba]/20 hover:border-[#0070ba]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              {payLoading === 'paypal' ? (
                <span className="w-5 h-5 border-2 border-[#0070ba]/30 border-t-[#0070ba] rounded-full animate-spin" />
              ) : (
                <div className="w-8 h-8 bg-[#0070ba] rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-[10px]">PP</span>
                </div>
              )}
              <div className="text-left">
                <p className="text-[12px] font-black text-white uppercase tracking-wide leading-none">PayPal</p>
                <p className="text-[9px] text-white/40 mt-0.5">Tarjeta internacional · Sin cuenta requerida</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black text-[#0070ba]">USD {precio}</p>
              <p className="text-[8px] text-white/25">{totalEmails} usuario{totalEmails > 1 ? "s" : ""}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CAMBIO 1: Agregar componente AdminPanel ANTES de function App() ──────────
const AdminPanel = ({ user }) => {
  const { session } = useSession();
  const [datos, setDatos] = React.useState(null);
  const [cargando, setCargando] = React.useState(true);
  const [busqueda, setBusqueda] = React.useState("");
  const [filtro, setFiltro] = React.useState("todos"); // todos | pro | free | pendiente
  const [metricas, setMetricas] = React.useState(null);


  React.useEffect(() => {
    cargarDatos();
    const interval = setInterval(cargarDatos, 60000);
    return () => clearInterval(interval);
  }, []);

  const cambiarPlan = async (targetEmail, nuevoPlan) => {
    const accion = nuevoPlan === "premium" ? "dar de alta PRO" : "dar de baja a FREE";
    if (!window.confirm(`¿Querés ${accion} a ${targetEmail}?`)) return;
    try {
      const res = await fetch(`${USUARIOS_URL}/cambiar-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adminEmail: user?.primaryEmailAddress?.emailAddress,
          targetEmail,
          nuevoPlan,
        }),
      });

      // El endpoint de cambiar-plan ahora devuelve directamente el resultado de la operación, 
      // sin necesidad de hacer una consulta adicional para verificar el cambio. 
      // Esto optimiza el proceso y reduce la cantidad de llamadas al backend.
      const data = await res.json();
      if (data.success) {
        alert(`Usuario ${targetEmail} actualizado a ${nuevoPlan.toUpperCase()}`);
        setDatos(null); // Esto fuerza a que el panel se vuelva a cargar
        await cargarDatos();
      } else {
        alert("Error al cambiar el plan: " + (data.error || "Error desconocido"));
      }

    } catch (e) {
      console.error("Error cambiando plan:", e);
      alert("Error de conexión al intentar cambiar el plan");
    }
  };

  const eliminarUsuario = async (targetEmail) => {
    if (!window.confirm(`⚠️ ¿Eliminar permanentemente a ${targetEmail}?`)) return;
    try {
      const res = await fetch(`${USUARIOS_URL}/eliminar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adminEmail: user?.primaryEmailAddress?.emailAddress,
          targetEmail,
        }),
      });
      const data = await res.json();
      if (data.success) await cargarDatos();
    } catch (e) {
      console.error("Error eliminando usuario:", e);
    }
  };

  const eliminarMensajeForo = async (mensajeId) => {
    if (!window.confirm("¿Eliminar este mensaje del foro?")) return;
    try {
      const res = await fetch(`${FORO_URL}/admin/eliminar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adminEmail: user?.primaryEmailAddress?.emailAddress,
          mensajeId,
        }),
      });
      const data = await res.json();
      if (data.success) await cargarDatos();
    } catch (e) {
      console.error("Error eliminando mensaje:", e);
    }
  };

  const cargarDatos = async () => {
    try {
      const token = await session.getToken();

      const res = await fetch(`${USUARIOS_URL}/admin`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (data.error) {
        console.error("Error del servidor:", data.error);
        return;
      }
      setDatos(data);

      const resM = await fetch(`${BASE}/api/metricas?email=${encodeURIComponent(user?.primaryEmailAddress?.emailAddress)}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const dataM = await resM.json();
      if (!dataM.error) setMetricas(dataM);

    } catch (e) {
      console.error("Error de conexión:", e);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-AR", {
      day: "2-digit", month: "short", year: "numeric"
    });
  };

  const usuariosFiltrados = datos?.usuarios?.filter(u => {
    const matchBusqueda = !busqueda ||
      u.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
      u.email?.toLowerCase().includes(busqueda.toLowerCase());
    const matchFiltro =
      filtro === "todos" ||
      (filtro === "pro" && u.plan === "premium") ||
      (filtro === "free" && u.plan === "free");
    return matchBusqueda && matchFiltro;
  }) || [];

  if (cargando) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto text-left">
      {/* HeaderAndres */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-amber-500">
          <Shield size={14} />
          <span className="text-[9px] font-black uppercase tracking-[0.3em]">Administración</span>
        </div>
        <h2 className="text-3xl font-light italic uppercase tracking-tighter text-white">
          Panel <span className="text-amber-500 font-bold not-italic font-black">Admin</span>
        </h2>
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em] mt-1">
          Solo visible para administradores
        </p>
      </div>

      {/* KPIsAndres */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {[
          { label: "Total Usuarios", value: datos?.total || 0, accent: "text-blue-400", top: "bg-blue-500", icon: <Users size={16} /> },
          { label: "Plan PRO", value: datos?.totalPro || 0, accent: "text-emerald-400", top: "bg-emerald-500", icon: <Zap size={16} /> },
          { label: "Plan FREE", value: datos?.totalFree || 0, accent: "text-amber-400", top: "bg-amber-500", icon: <Users size={16} /> },
          { label: "PRO Pendiente", value: datos?.proPendiente || 0, accent: "text-purple-400", top: "bg-purple-500", icon: <Clock size={16} /> },
        ].map((kpi) => (
          <div key={kpi.label} className="relative bg-white/3 border border-white/7 rounded-2xl p-4 flex items-center gap-3 overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-[2px] ${kpi.top} rounded-t-2xl`} />
            <div className={`bg-white/5 p-2 rounded-xl shrink-0 ${kpi.accent}`}>{kpi.icon}</div>
            <div>
              <p className={`text-xl font-black ${kpi.accent}`}>{kpi.value}</p>
              <p className="text-[9px] text-white/25 uppercase tracking-widest">{kpi.label}</p>
            </div>
          </div>
        ))}
      </div>
            // Agregar el bloque visual después de los 4 KPIs de usuarios:
      {
        metricas && (
          <div className="mb-8">
            <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.2em] mb-3">
              — Monitor de Uso IA · Claude Haiku 4.5
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {[
                {
                  label: "Generaciones",
                  value: metricas.totalGeneraciones.toLocaleString(),
                  accent: "text-blue-400",
                  top: "bg-blue-500",
                },
                {
                  label: "Tokens Input",
                  value: (metricas.totalTokensInput / 1000).toFixed(1) + "K",
                  accent: "text-cyan-400",
                  top: "bg-cyan-500",
                },
                {
                  label: "Tokens Output",
                  value: (metricas.totalTokensOutput / 1000).toFixed(1) + "K",
                  accent: "text-purple-400",
                  top: "bg-purple-500",
                },
                {
                  label: "Costo API Total",
                  value: `USD $${metricas.costoTotalUSD}`,
                  accent: "text-red-400",
                  top: "bg-red-500",
                },
              ].map((kpi) => (
                <div key={kpi.label} className="relative bg-white/3 border border-white/7 rounded-2xl p-4 overflow-hidden">
                  <div className={`absolute top-0 left-0 right-0 h-[2px] ${kpi.top} rounded-t-2xl`} />
                  <p className={`text-xl font-black ${kpi.accent}`}>{kpi.value}</p>
                  <p className="text-[9px] text-white/25 uppercase tracking-widest">{kpi.label}</p>
                </div>
              ))}
            </div>

            {/* Resumen financiero */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4">
                <p className="text-[9px] text-emerald-400/60 uppercase tracking-widest mb-1">Ingresos PRO</p>
                <p className="text-xl font-black text-emerald-400">USD ${metricas.ingresosUSD}</p>
                <p className="text-[9px] text-white/20">{metricas.totalUsuariosPro} usuarios × $20</p>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4">
                <p className="text-[9px] text-red-400/60 uppercase tracking-widest mb-1">Costo API</p>
                <p className="text-xl font-black text-red-400">USD ${metricas.costoTotalUSD}</p>
                <p className="text-[9px] text-white/20">Claude Haiku acumulado</p>
              </div>
              <div className={`${metricas.margenUSD >= 0 ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"} border rounded-2xl p-4`}>
                <p className={`text-[9px] uppercase tracking-widest mb-1 ${metricas.margenUSD >= 0 ? "text-emerald-400/60" : "text-red-400/60"}`}>Margen Neto</p>
                <p className={`text-xl font-black ${metricas.margenUSD >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {metricas.margenUSD >= 0 ? "+" : ""}USD ${metricas.margenUSD}
                </p>
                <p className="text-[9px] text-white/20">Ingresos − Costo API</p>
              </div>
            </div>

            {/* Últimos 7 días */}
            {metricas.ultimosDias?.length > 0 && (
              <div className="bg-white/3 border border-white/8 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-4 px-4 py-2 border-b border-white/5 bg-white/3">
                  <span className="text-[9px] font-black text-white/30 uppercase">Fecha</span>
                  <span className="text-[9px] font-black text-white/30 uppercase">Generaciones</span>
                  <span className="text-[9px] font-black text-white/30 uppercase">Tokens</span>
                  <span className="text-[9px] font-black text-white/30 uppercase">Costo</span>
                </div>
                {metricas.ultimosDias.slice(0, 7).map((dia) => (
                  <div key={dia.fecha} className="grid grid-cols-4 px-4 py-2 border-b border-white/5 last:border-0 hover:bg-white/3">
                    <span className="text-[10px] text-white/50">{dia.fecha}</span>
                    <span className="text-[10px] text-blue-400 font-bold">{dia.generaciones}</span>
                    <span className="text-[10px] text-white/40">{((dia.tokensInput + dia.tokensOutput) / 1000).toFixed(1)}K</span>
                    <span className="text-[10px] text-red-400">${dia.costoUSD.toFixed(4)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      }
      {/* PRO Pendientes */}
      {datos?.pendientes?.length > 0 && (
        <div className="bg-purple-500/5 border border-purple-500/20 rounded-[24px] p-5 mb-6">
          <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Clock size={12} /> PRO Pendientes — Pagaron pero no se registraron
          </p>
          <div className="space-y-2">
            {datos.pendientes.map((p, i) => (
              <div key={i} className="flex items-center justify-between bg-black/30 border border-white/5 rounded-xl px-4 py-2">
                <span className="text-[11px] text-white font-bold">{p.email}</span>
                <span className="text-[9px] text-purple-400">{formatearFecha(p.fechaPago)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-3 py-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-slate-500" />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            className="bg-transparent outline-none text-[11px] text-white w-full placeholder:text-slate-600"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {[
            { id: "todos", label: "Todos" },
            { id: "pro", label: "PRO" },
            { id: "free", label: "FREE" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFiltro(f.id)}
              className={`px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${filtro === f.id
                ? "bg-blue-600 text-white"
                : "bg-white/5 text-white/40 hover:text-white border border-white/10"
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={cargarDatos}
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white transition-all"
          title="Actualizar"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      {/* Activar PRO manual */}
      <div className="bg-white/3 border border-white/8 rounded-[24px] p-5 mb-6">
        <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Zap size={12} className="text-amber-400" /> Activar PRO manualmente
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            id="admin-email-pro"
            placeholder="email@usuario.com"
            className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[11px] text-white placeholder:text-white/20 outline-none focus:border-amber-500/50"
          />
          <button
            onClick={() => {
              const email = document.getElementById("admin-email-pro").value.trim();
              if (!email) return;
              cambiarPlan(email, "premium");
              document.getElementById("admin-email-pro").value = "";
            }}
            className="px-4 py-2.5 bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 text-amber-400 font-black text-[9px] uppercase tracking-widest rounded-xl transition-all"
          >
            Dar PRO
          </button>
        </div>
      </div>
      {/* Tabla de usuarios */}
      <div className="bg-white/3 border border-white/8 rounded-[24px] overflow-hidden">
        {/* Header tabla */}
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/5 bg-white/3">
          <span className="col-span-4 text-[9px] font-black text-white/30 uppercase tracking-widest">Usuario</span>
          <span className="col-span-4 text-[9px] font-black text-white/30 uppercase tracking-widest">Email</span>
          <span className="col-span-2 text-[9px] font-black text-white/30 uppercase tracking-widest">Registro</span>
          <span className="col-span-2 text-[9px] font-black text-white/30 uppercase tracking-widest">Plan</span>
          <span className="col-span-2 text-[9px] font-black text-white/30 uppercase tracking-widest">Plan</span>
          <span className="col-span-2 text-[9px] font-black text-white/30 uppercase tracking-widest">Acciones</span>
        </div>

        {/* Filas */}
        {usuariosFiltrados.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[11px] text-white/20 uppercase tracking-widest">Sin resultados</p>
          </div>
        ) : (
          usuariosFiltrados.map((u, i) => (
            <div
              key={u.email}
              className={`grid grid-cols-12 gap-2 px-5 py-3 items-center ${i !== usuariosFiltrados.length - 1 ? "border-b border-white/5" : ""
                } hover:bg-white/3 transition-all`}
            >
              {/* Nombre */}
              <div className="col-span-4 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-600/20 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <span className="text-[9px] font-black text-blue-400">
                    {u.nombre?.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase() || "??"}
                  </span>
                </div>
                <span className="text-[11px] font-bold text-white truncate">{u.nombre}</span>
              </div>

              {/* Email */}
              <div className="col-span-4">
                <span className="text-[10px] text-white/50 truncate block">{u.email}</span>
              </div>

              {/* Fecha */}
              <div className="col-span-2">
                <span className="text-[10px] text-white/30">{formatearFecha(u.fechaRegistro)}</span>
              </div>

              {/* Plan */}
              <div className="col-span-2 flex items-center gap-2">
                <span className={`text-[9px] font-black px-2 py-1 rounded-lg uppercase tracking-wide ${u.plan === "premium"
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                  : "bg-white/5 text-white/30 border border-white/10"
                  }`}>
                  {u.plan === "premium" ? "PRO" : "FREE"}
                </span>
              </div>

              {/* Acciones */}
              <div className="col-span-2 flex items-center gap-1">
                {u.plan === "premium" ? (
                  <button
                    onClick={() => cambiarPlan(u.email, "free")}
                    className="text-[8px] font-black px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20 transition-all uppercase"
                  >
                    Bajar
                  </button>
                ) : (
                  <button
                    onClick={() => cambiarPlan(u.email, "premium")}
                    className="text-[8px] font-black px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-all uppercase"
                  >
                    PRO ↑
                  </button>
                )}
                <button
                  onClick={() => eliminarUsuario(u.email)}
                  className="p-1 text-white/15 hover:text-red-400 transition-colors"
                  title="Eliminar usuario"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <p className="text-[9px] text-white/15 text-center uppercase tracking-widest mt-4">
        {usuariosFiltrados.length} de {datos?.total || 0} usuarios · Actualiza cada 60 segundos
      </p>
    </div>
  );
};

// ==========================================
// --- PARTE 3: EL COMPONENTE PRINCIPAL ---
// ==========================================
function App() {
  // Free daily usage limit management using localStorage
  const FREE_DAILY_LIMIT = 1;
  // Obtiene el contador de generaciones del día actual desde localStorage
  const getDailyCount = () => {
    const today = new Date().toDateString();
    const stored = JSON.parse(localStorage.getItem("orquest_daily") || "{}");
    if (stored.date !== today) return 0;
    return stored.count || 0;
  };

  // Incrementa el contador diario de generaciones en localStorage
  const incrementDailyCount = () => {
    const today = new Date().toDateString();
    const count = getDailyCount() + 1;
    localStorage.setItem(
      "orquest_daily",
      JSON.stringify({ date: today, count }),
    );
  };

  const [rapObjects, setRapObjects] = useState(null);
  const [capObjects, setCapObjects] = useState(null);
  const [dailyCount, setDailyCount] = useState(getDailyCount);

  const [activeManual, setActiveManual] = useState(null);
  const [recentTools, setRecentTools] = useState([]);
  // ==========================================
  // --- SECTION 3: STATE & HOOKS ---
  // ==========================================
  const { user } = useUser();
  const { signOut } = useClerk();
  const { session } = useSession();
  const [instanceId] = useState(() => {
    try {
      const savedId = localStorage.getItem("orquest_session_id");
      if (savedId) return savedId;
      const newId = crypto.randomUUID();
      localStorage.setItem("orquest_session_id", newId);
      return newId;
    } catch (e) {
      return crypto.randomUUID();
    }
  });
  // Cierra sesión: notifica al backend para limpiar sesión activa, luego cierra en Clerk
  const handleLogout = async () => {
    try {
      await fetch(API_URL.replace("/api/generate", "/api/logout"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user?.primaryEmailAddress?.emailAddress,
        }),
      });
    } catch (e) {
      console.log("Logout server error:", e);
    }
    signOut();
  };
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [payLoading, setPayLoading] = useState(null); // 'mp' | 'paypal' | null
  const [mpCotizacion, setMpCotizacion] = useState(null);
  const [emailsAdicionales, setEmailsAdicionales] = useState([]);
  const [nuevoEmail, setNuevoEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState("");


  const [feedbackSent, setFeedbackSent] = useState(false);
  const [tempTableName, setTempTableName] = useState("");
  const [tempFieldName, setTempFieldName] = useState("");
  const [tempFieldLabel, setTempFieldLabel] = useState("");
  const [tempFieldType, setTempFieldType] = useState("");
  const [refineInput, setRefineInput] = useState("");
  const [activeView, setActiveView] = useState("workspace");
  const [selectedTool, setSelectedTool] = useState(null);
  const [input, setInput] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [copied, setCopied] = useState(false);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [wizardData, setWizardData] = useState(initialWizardData);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });
  const [isListening, setIsListening] = useState(false);
  const chatEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isPro = user?.publicMetadata?.plan === "premium";
  const isAdmin = user?.publicMetadata?.role === "admin";
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [latency, setLatency] = useState(22);
  const [visits, setVisits] = useState(() => {
    const saved = localStorage.getItem("orquest_visits");
    return saved ? parseInt(saved) : 0;
  });

  const handleSendFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSendingFeedback(true);
    try {
      const response = await fetch(
        `${API_URL.replace("/api/generate", "/api/sugerencia")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user?.primaryEmailAddress?.emailAddress,
          usuario: user?.fullName,
          mensaje: feedbackText,
        }),
      });
      if (response.ok) {
        showNotification("Sugerencia enviada. ¡Gracias!", "success");
        setIsFeedbackOpen(false);
        setFeedbackText("");
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || "Error en el servidor", "error");
      }
    } catch (e) {
      console.error("Error al enviar:", e);
      showNotification("Error de conexión", "error");
    } finally {
      setSendingFeedback(false);
    }
    setFeedbackText("");
    setFeedbackSent(true);
    setTimeout(() => {
      setFeedbackSent(false);
      setIsFeedbackOpen(false);
    }, 2500);
  };

  const [tempHotspot, setTempHotspot] = useState({ tech: "", label: "" });

  const handleAddHotspot = () => {
    if (!tempHotspot.tech) return;
    const newItem = {
      tech: tempHotspot.tech.toUpperCase(),
      label: tempHotspot.label || tempHotspot.tech.toUpperCase(),
    };
    setWizardData((prev) => ({
      ...prev,
      alvHotspotList: [...(prev.alvHotspotList || []), newItem],
    }));
    setTempHotspot({ tech: "", label: "" });
  };

  const handleRemoveHotspot = (index) => {
    setWizardData((prev) => ({
      ...prev,
      alvHotspotList: prev.alvHotspotList.filter((_, i) => i !== index),
    }));
  };

  // --- FIX SEGURIDAD #9: SILENCIADOR DE LOGS ---
  useEffect(() => {
    if (import.meta.env.PROD) {
      // En producción, estas funciones no harán nada
      console.log = () => { };
      console.debug = () => { };
      console.info = () => { };
      console.warn = () => { };
      // Dejamos console.error por si ocurre algo crítico, 
      // pero idealmente también deberías limitarlo.
    }
  }, []);

  // Capturar resultado de PayPal después del pago exitoso y recargar la página para reflejar el cambio de plan
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const token = params.get("token"); // PayPal envía el orderId como token

    if (status === "approved" && token) {
      window.history.replaceState({}, "", "/");
      // Capturar el pago
      fetch(`${PAYPAL_URL}/capturar-orden-multi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: token }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            showNotification("🎉 ¡Plan PRO activado! Recargando...", "success");
            setTimeout(() => window.location.reload(), 2000);
          }
        })
        .catch((err) => console.error("Error captura:", err));
    }
  }, []);

  // Mostrar botón de scroll to top al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current?.scrollTop > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    const container = scrollContainerRef.current;
    if (container) container.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, []);

  // Registrar visita en el backend y contar visitas totales
  useEffect(() => {
    if (!user) return;
    const userEmail = user.primaryEmailAddress?.emailAddress;

    const registrarVisita = async () => {
      try {
        // Registrar visita:
        const res = await fetch(`${API_URL.replace("/api/generate", "/api/visits")}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: userEmail }),
        });

        const data = await res.json();
        setVisits(data.total);

        // Registrar usuario en BD:
        await fetch(`${USUARIOS_URL}/registrar`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nombre: user?.fullName || "Consultor SAP",
            email: userEmail,
          }),
        }).catch(e => console.log("Error registrando usuario:", e));

      } catch {
        // fallback a localStorage
        const current = parseInt(
          localStorage.getItem("orquest_visits") || "1000",
        );
        setVisits(current);
      }
    };

    registrarVisita();
  }, [user]);

  const [isFavorite, setIsFavorite] = useState(false);

  // Resetear favorito al cambiar de herramienta
  useEffect(() => {
    setIsFavorite(false);
  }, [result]);

  // Atajos de teclado: Ctrl+Enter para generar, Esc para cerrar herramienta
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === "Enter" &&
        selectedTool &&
        isPro &&
        !loading
      ) {
        handleGenerate();
      }
      if (e.key === "Escape" && selectedTool) {
        selectToolHandler(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedTool, isPro, loading, input, wizardData]);

  // Simular latencia variable entre 19 y 32 segundos para mostrar en la UI
  useEffect(() => {
    const interval = setInterval(() => {
      setLatency(Math.floor(Math.random() * (32 - 19 + 1)) + 19);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Scroll automático en el chat: si el último mensaje es del usuario, hacer scroll al inicio del nuevo mensaje;
  // si es del sistema, hacer scroll al final para mostrar la respuesta completa
  useEffect(() => {
    if (isChatOpen && chatHistory.length > 0) {
      const lastMessage = chatHistory[chatHistory.length - 1];
      if (lastMessage.role === "user") {
        chatEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        chatEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [chatHistory, chatLoading]);

  // Cargar tablas guardadas en localStorage al iniciar la aplicación
  useEffect(() => {
    const savedTables = localStorage.getItem("orquest_z_tables");
    if (savedTables) {
      setWizardData((prev) => ({
        ...prev,
        progTables: JSON.parse(savedTables),
      }));
    }
  }, []);

  // Guardar tablas en localStorage cada vez que se actualizan en el wizard de programas Z
  useEffect(() => {
    if (wizardData.progTables?.length > 0) {
      localStorage.setItem(
        "orquest_z_tables",
        JSON.stringify(wizardData.progTables),
      );
    }
  }, [wizardData.progTables]);

  // Cargar tablas guardadas en localStorage al iniciar la aplicación (para herramientas que no sean programas Z)
  useEffect(() => {
    const tablasGuardadas = localStorage.getItem("mis_tablas_sap");
    if (tablasGuardadas) {
      setWizardData((prev) => ({
        ...prev,
        progTables: JSON.parse(tablasGuardadas),
      }));
    }
  }, []);

  // Guardar tablas en localStorage cada vez que se actualizan (para herramientas que no sean programas Z)
  useEffect(() => {
    if (wizardData.progTables) {
      localStorage.setItem(
        "mis_tablas_sap",
        JSON.stringify(wizardData.progTables),
      );
    }
  }, [wizardData.progTables]);

  // Inicializar Mermaid para diagramas, con tema oscuro personalizado
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'dark',
      themeVariables: {
        primaryColor: '#0047AB',
        primaryTextColor: '#ffffff',
        lineColor: '#4B5563',
        background: '#0B0F1A',
      }
    });
  }, []);

  // --- END SECTION 3 ---

  // ==========================================
  // --- SECTION 4: ACTIONS ---
  // ==========================================
  // Abre el modal de selección de método de pago (PayPal / Mercado Pago)
  const handleMercadoPagoSubscription = () => {
    setIsPayModalOpen(true);
  };
  // Crea preferencia de pago en Mercado Pago y redirige al checkout en ARS
  const pagarConMP = async () => {
    try {
      setPayLoading('mp');
      const response = await fetch(`${MP_URL}/crear-preferencia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user?.primaryEmailAddress?.emailAddress,
        }),
      });
      const data = await response.json();
      if (data.link) {
        setMpCotizacion(data.cotizacion);
        window.open(data.link, '_blank');
      } else {
        showNotification("Error al crear pago: " + (data.error || "Error desconocido"), "error");
      }
    } catch (error) {
      showNotification("Error de red. Verificá tu conexión.", "error");
    } finally {
      setPayLoading(null);
    }
  };
  // Crea orden de pago en PayPal y redirige al checkout en USD
  const pagarConPayPal = async (emailsAdicionales = []) => {
    try {
      setPayLoading('paypal');
      const response = await fetch(`${PAYPAL_URL}/crear-orden`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user?.primaryEmailAddress?.emailAddress,
        }),
      });
      const data = await response.json();
      if (data.link) {
        window.open(data.link, '_blank');
      } else {
        showNotification("Error al crear orden PayPal: " + (data.error || "Error desconocido"), "error");
      }
    } catch (error) {
      showNotification("Error de red. Verificá tu conexión.", "error");
    } finally {
      setPayLoading(null);
    }
  };

  // Hace scroll hacia el inicio del contenedor principal
  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };
  // Resetea todo el workspace: resultado, input, wizard y hotspots
  const handleClearAll = () => {
    setResult("");
    setRapObjects(null);
    setCapObjects(null);
    setInput("");
    setRefineInput("");
    setWizardData({ ...initialWizardData, _capReset: Date.now() });
    if (typeof setTempHotspot === "function") {
      setTempHotspot({ tech: "", label: "" });
    }
    showNotification("Espacio de trabajo reseteado", "info");
  };
  // Muestra notificación flotante. type: "info" | "success" | "error"
  const showNotification = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "info" }),
      4000,
    );
  };
  // Genera el header estándar SAP ABAP con nombre de programa, autor y fecha
  const getSAPHeader = () => {
    const date = new Date().toLocaleDateString();
    const author = user?.fullName || "CONSULTOR SAP";
    const programName = `ZORQUEST_${selectedTool?.id.toUpperCase() || "PROG"}`;
    return `*&---------------------------------------------------------------------*
*& Report  ${programName}
*&---------------------------------------------------------------------*
*& Descripción: ${selectedTool?.description || "Programa Generado"}
*& Autor:       ${author}
*& Fecha:       ${date}
*& Generado por: ORQUEST SAP ARCHITECT v9.0
*&---------------------------------------------------------------------*

`;
  };
  // Descarga el código generado como archivo .abap
  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([result], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `ORQUEST_${selectedTool?.id.toUpperCase()}_${Date.now()}.abap`;
    document.body.appendChild(element);
    element.click();
    showNotification("Archivo .abap generado con éxito", "success");
  };

  // ============================================================
  // FIX 3: HELPER — Decodifica Base64 a texto plano UTF-8
  // Soporta tildes, ñ y caracteres especiales del español
  // ============================================================
  // Decodifica string Base64 a texto UTF-8 con soporte de tildes y ñ
  const decodeBase64ToString = (base64) => {
    try {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder("utf-8").decode(bytes);
    } catch (e) {
      console.error("❌ Error decodificando Base64:", e);
      return "";
    }
  };

  // ============================================================
  // FIX 1: handleFileUpload — ahora guarda el objeto File nativo
  // en fileContext.file para que leerArchivoComoTexto() pueda usarlo
  // ============================================================
  // Procesa archivo adjunto: valida tamaño máximo 2MB, lee como Base64 y guarda File nativo
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2000000) {
      showNotification(
        "ARCHIVO MUY GRANDE: Los Word con imágenes fallan. Intenta quitar las fotos del documento.",
        "error",
      );
      return;
    }

    setWizardData((prev) => ({ ...prev, fileStatus: "uploading" }));

    const reader = new FileReader();
    reader.onload = async (event) => {
      const fullBase64 = event.target.result;
      const cleanBase64 = fullBase64.split(",")[1];

      setWizardData((prev) => ({
        ...prev,
        fileContext: {
          name: file.name,
          type: file.type,
          size: (file.size / 1024).toFixed(2) + " KB",
          content: cleanBase64,
          file: file, // ← FIX 1: objeto File nativo guardado
        },
        fileStatus: "ready",
      }));
      showNotification(`Archivo "${file.name}" cargado con éxito`, "success");
    };
    reader.readAsDataURL(file);
  };
  // Elimina el archivo adjunto del wizard y resetea el estado del uploader a "pending"
  const removeUploadedFile = () => {
    setWizardData((prev) => ({
      ...prev,
      fileContext: null,
      fileStatus: "pending",
    }));
  };
  // Alterna el estado de favorito del resultado actual
  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
    showNotification(
      !isFavorite ? "Agregado a favoritos" : "Eliminado de favoritos",
      "info",
    );
  };

  // ============================================================
  // handleGenerate — Motor principal con FIX 2 aplicado
  // ============================================================
  // Motor principal: verifica límite FREE, construye prompt, lee archivo, llama a la API
  const handleGenerate = async (isRefining = false) => {

    const token = await session.getToken({});
    setLoading(true);

    let userInput = isRefining ? refineInput || "" : input || "";
    if (!isRefining) setResult("");

    const { fileContext, ...otrosDatos } = wizardData;

    // ─────────────────────────────────────────────────────────────
    // BLOQUE 1: LECTURA Y NORMALIZACIÓN DEL ARCHIVO ADJUNTO
    // Soporta: TXT, CSV, PDF, DOCX, XLSX, XLS
    // ─────────────────────────────────────────────────────────────
    const leerArchivoComoTexto = async (file) => {
      if (!file) return null;
      const ext = file.name.split(".").pop().toLowerCase();

      if (ext === "txt" || ext === "csv") {
        return await file.text();
      }

      if (ext === "pdf") {
        try {
          const pdfModule = await import("pdfjs-dist");
          const pdfLib = pdfModule.default || pdfModule;

          if (!pdfLib || !pdfLib.getDocument) {
            throw new Error("pdfjs-dist no se cargó correctamente");
          }

          // Usar siempre versión compatible con el paquete instalado
          if (pdfLib.GlobalWorkerOptions) {
            const version = pdfLib.version || "3.11.174";
            // pdfjs 4.x+ usa un worker distinto
            const workerUrl = version.startsWith("3")
              ? `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`
              : `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
            pdfLib.GlobalWorkerOptions.workerSrc = workerUrl;
          }

          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfLib.getDocument({
            data: arrayBuffer,
            useWorkerFetch: false,
            isEvalSupported: false,
            useSystemFonts: true,
          }).promise;

          const paginas = [];
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const lineaTexto = content.items.map((item) => item.str).join(" ");
            paginas.push(`[Página ${i}]\n${lineaTexto}`);
          }
          return paginas.join("\n\n");
        } catch (err) {
          console.error("❌ Error leyendo PDF:", err.message);
          return `[ERROR AL LEER PDF: ${err.message}. Convertí el archivo a .docx y volvé a intentarlo]`;
        }
      }
      //andres
      if (ext === "docx") {
        const mammoth = (await import("mammoth")).default;
        const arrayBuffer = await file.arrayBuffer();
        const resultado = await mammoth.extractRawText({ arrayBuffer });
        return resultado.value;
      }
      //andres
      if (ext === "xlsx" || ext === "xls") {
        const XLSX = (await import("xlsx")).default;
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const hojas = [];
        workbook.SheetNames.forEach((nombreHoja) => {
          const hoja = workbook.Sheets[nombreHoja];
          const csv = XLSX.utils.sheet_to_csv(hoja, { FS: " | " });
          if (csv.trim()) hojas.push(`[Hoja: ${nombreHoja}]\n${csv}`);
        });
        return hojas.join("\n\n");
      }

      return await file.text().catch(() => null);
    };

    // ─────────────────────────────────────────────────────────────
    // BLOQUE 2: ANÁLISIS INTELIGENTE DE LA ESPECIFICACIÓN SAP
    // ─────────────────────────────────────────────────────────────
    const analizarEspecificacionSAP = (texto) => {
      if (!texto) return {};

      const patrones = {
        tipoObjeto: [
          /\b(report|program|function\s*module|class\s*abap|method|include|form\s*routine|user\s*exit|badi|enhancement|module\s*pool|smartform|adobe\s*form|bdc|lsmw|idoc|amdp)\b/gi,
        ],
        nombreObjeto: [
          /(?:nombre|name|programa|program|función|function|clase|class|reporte|report)[:\s]+([A-Z][A-Z0-9_\/]{2,40})/gi,
          /\b(Z[A-Z0-9_]{2,39}|Y[A-Z0-9_]{2,39})\b/g,
        ],
        transacciones: [
          /\b(SE38|SE24|SE37|SE80|SE11|SE16|SM30|SM31|ME21N|ME22N|ME23N|VA01|VA02|VA03|FB01|FB60|MM01|XD01|XK01|SU01|SPRO|BD87|WE02|WE19|VF01|VF02|IW31|IW32|CO01|CO02|MB51|MIGO|J1IIN|F110|F-53)\b/gi,
        ],
        tablasEstandar: [
          /\b(MARA|MARC|MAKT|MARD|MBEW|VBAK|VBAP|VBFA|EKKO|EKPO|EBAN|EINA|LFA1|KNA1|BKPF|BSEG|COEP|COSS|COSP|PRPS|PROJ|AFKO|AFPO|QMEL|QMFE|QMMA|IFLO|IFLOT|ILOA|EQUI|EQKT|T001|T001L|T005T|FEBEP|BSID)\b/gi,
        ],
        tablasZ: [/\b(Z[A-Z0-9_]{2,39})\b/g],
        moduloSAP: [
          /\b(MM|SD|FI|CO|PP|PM|QM|WM|EWM|HCM|HR|PS|CRM|SCM|BI|BW|HANA|S\/4HANA|BASIS|ABAP|RE-FX|IS-U|IS-H|CS|SM|FI-AR)\b/g,
        ],
        camposSeleccion: [
          /(?:pantalla\s*de\s*selección|selection[\s-]*screen|parámetros)[:\s]+([^\n]{10,200})/gi,
        ],
        logicaPrincipal: [
          /(?:lógica|logic|proceso|process|descripción|description|objetivo|objective|funcionalidad|functionality)[:\s]+([^\n]{15,300})/gi,
        ],
        condicionesSQL: [
          /WHERE\s+([^\n]{10,150})/gi,
          /(?:filtrar?\s*por|filter\s*by)[:\s]+([^\n]{10,100})/gi,
        ],
        autor: [
          /(?:autor|author|solicitante|requester|creado\s*por|created\s*by|desarrollado\s*por)[:\s]+([^\n]{3,60})/gi,
        ],
        fecha: [
          /(?:fecha|date|creación|created)[:\s]+(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\d{4}[\/\-\.]\d{2}[\/\-\.]\d{2})/gi,
        ],
        mensajesError: [
          /(?:mensaje|message|error|m_class)[:\s]+([^\n]{5,80})/gi,
        ],
      };

      const resultado = {};
      for (const [clave, listaPatrones] of Object.entries(patrones)) {
        const matches = [];
        for (const patron of listaPatrones) {
          const encontrados = [...texto.matchAll(patron)];
          encontrados.forEach((m) => {
            const valor = (m[1] || m[0]).trim().substring(0, 200);
            if (valor && !matches.includes(valor)) matches.push(valor);
          });
          if (matches.length > 0) break;
        }
        if (matches.length > 0) {
          resultado[clave] = [...new Set(matches)].slice(0, 8);
        }
      }
      return resultado;
    };

    // ─────────────────────────────────────────────────────────────
    // BLOQUE 3: MOTOR DE INGENIERÍA DE PROMPTS V4
    // FIX 2 aplicado: resolución correcta de contenido (File nativo + Base64)
    // ─────────────────────────────────────────────────────────────
    const construirPromptTecnico = async () => {
      // ── FIX 2: Resolución de contenido del archivo ──────────────
      let contenidoEspecificacion = "";

      if (fileContext?.file instanceof File) {
        // CASO A — Objeto File nativo disponible (flujo nuevo con FIX 1)
        try {
          const textoLeido = await leerArchivoComoTexto(fileContext.file);
          contenidoEspecificacion = textoLeido || "";
        } catch (errArchivo) {
          console.error(
            "❌ Error leyendo File nativo, fallback a Base64:",
            errArchivo,
          );
          contenidoEspecificacion = fileContext.content
            ? decodeBase64ToString(fileContext.content)
            : "";
        }
      } else if (
        typeof fileContext?.content === "string" &&
        fileContext.content.trim()
      ) {
        // CASO B — Solo hay Base64 (archivos subidos antes del fix)
        contenidoEspecificacion = decodeBase64ToString(fileContext.content);
      }
      // ── Fin FIX 2 ───────────────────────────────────────────────

      console.log(
        "📝 contenidoEspecificacion (primeros 300):",
        contenidoEspecificacion.substring(0, 300),
      );

      const LIMITE_CHARS = 12000;
      const especTruncada =
        contenidoEspecificacion.length > LIMITE_CHARS
          ? contenidoEspecificacion.substring(0, LIMITE_CHARS) +
          "\n\n[ESPECIFICACIÓN TRUNCADA]"
          : contenidoEspecificacion;

      const seccionesSAP = analizarEspecificacionSAP(contenidoEspecificacion);

      const formatearSeccion = (clave, label) => {
        const val = seccionesSAP[clave];
        if (!val || val.length === 0)
          return `- ${label}: [NO ESPECIFICADO — A COMPLETAR POR EL DESARROLLADOR]`;
        return `- ${label}: ${val.join(", ")}`;
      };

      const resumenSecciones = `
${formatearSeccion("tipoObjeto", "Tipo de objeto ABAP")}
${formatearSeccion("nombreObjeto", "Nombre del objeto")}
${formatearSeccion("moduloSAP", "Módulo SAP")}
${formatearSeccion("transacciones", "Transacciones involucradas")}
${formatearSeccion("tablasEstandar", "Tablas estándar SAP detectadas")}
${formatearSeccion("tablasZ", "Tablas Z / custom detectadas")}
${formatearSeccion("camposSeleccion", "Parámetros de selección")}
${formatearSeccion("logicaPrincipal", "Lógica / proceso principal")}
${formatearSeccion("condicionesSQL", "Condiciones de filtrado/WHERE")}
${formatearSeccion("mensajesError", "Mensajes / clases de mensajes")}
${formatearSeccion("autor", "Autor / solicitante")}
${formatearSeccion("fecha", "Fecha de especificación")}
${wizardData?.fioriPlatform ? `- Plataforma Fiori: ${wizardData.fioriPlatform}` : ""}
`.trim();

      const hayPedidoUsuario = userInput.trim().length > 0;
      const hayEspecificacion = especTruncada.trim().length > 0;

      // Si es ALV sin especificación ni pedido, inyectar pedido base
      const pedidoFinal =
        !hayPedidoUsuario && !hayEspecificacion && wizardData?.toolId === "alv"
          ? "Generá un reporte ALV completo y funcional con EKKO/EKPO. Incluí sociedad, número de pedido, proveedor, fecha y monto. Pantalla de selección con filtro por sociedad y rango de pedidos. Output CL_SALV_TABLE con columnas configuradas."
          : hayPedidoUsuario
            ? userInput
            : "[Sin pedido adicional — seguir 100% la especificación del documento]";

      // ── Instrucciones de versión ABAP según selección del usuario ──
      const abapVersion = wizardData?.abapVersion || "S4HANA";
      const instruccionesVersion =
        {
          S4HANA: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VERSIÓN ABAP TARGET: S/4HANA 2021+ (CLEAN ABAP ESTRICTO)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OBLIGATORIO usar estas tecnologías modernas:
- Clases locales (CLASS lcl_xxx DEFINITION/IMPLEMENTATION) — NO usar FORM/PERFORM
- Declaraciones inline: DATA(lv_var) = ... / FIELD-SYMBOLS: <lfs> TYPE ...
- CAST en lugar de ?= para downcasting
- Open SQL con @variable en WHERE y lista explícita de campos (NUNCA SELECT *)
- String templates: |texto { lv_var }| — NUNCA CONCATENATE
- VALUE #( ... ) para inicialización de estructuras y tablas
- lines( lt_tabla ) en lugar de DESCRIBE TABLE
- TRY...CATCH para excepciones OO — NUNCA MESSAGE E... sin TRY
- WITH EMPTY KEY en tablas internas cuando corresponda
- PROHIBIDO: FORM, PERFORM, MOVE-CORRESPONDING, SELECT *, DESCRIBE TABLE, CONCATENATE
`,
          ABAP750: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VERSIÓN ABAP TARGET: ABAP 7.50 (INLINE + VALUE #)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Usar estas características disponibles desde 7.50:
- Declaraciones inline: DATA(lv_var) = ...
- VALUE #( ... ) para inicialización
- String templates: |texto { var }|
- LOOP AT ... INTO DATA(ls_item)
- SELECT con lista de campos (sin SELECT *)
- FORM/PERFORM aceptado pero no obligatorio
- No es necesario usar clases locales
- Compatible con sistemas que NO son S/4HANA puro
`,
          ABAP740: `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VERSIÓN ABAP TARGET: ABAP 7.40 (ECC 6.0 COMPATIBLE)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Usar sintaxis clásica compatible con ECC 6.0 y versiones anteriores:
- FORM / PERFORM para modularización
- DATA: lv_var TYPE ... (declaraciones al inicio, NO inline)
- MOVE-CORRESPONDING permitido
- CONCATENATE para concatenar strings
- DESCRIBE TABLE lt_tabla LINES lv_count
- SELECT * permitido en contextos simples
- Mensajes con MESSAGE ... TYPE 'E'/'I'/'S'
- Sin VALUE #, sin string templates, sin clases OO obligatorias
`,
        }[abapVersion] || "";

      const promptFinal = selectedTool?.id === 'translator' ? input?.trim() || '' : `
[ROL: SAP ABAP S/4HANA SENIOR ARCHITECT — MODO GENERACIÓN ESTRICTA]

Generá código ABAP S/4HANA profesional y completo siguiendo las instrucciones exactas a continuación.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REGLAS DE GENERACIÓN — OBLIGATORIAS (NO NEGOCIABLES)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1. NUNCA inventés campos, tablas, módulos de función o lógicas que NO aparezcan en la especificación o en el pedido del usuario.
2. NUNCA agregués funcionalidades extra no solicitadas.
3. Si algún dato es ambiguo o falta en la especificación, insertá el comentario: "* TODO: [descripción de lo que falta]" en el punto exacto del código.
4. Trabajá SOLO con la información real del documento y del pedido.
5. Código limpio (Clean ABAP): sin variables obsoletas, sin SELECT *, sin MOVE-CORRESPONDING innecesario.
6. Todos los comentarios del código deben estar en español técnico SAP.
7. Incluí manejo de errores con MESSAGE y SY-SUBRC en cada operación crítica.
8. Si detectás tablas Z que no existen aún, declaralas como TYPE TABLE OF con estructura basada en el contexto, y añadí un comentario "* TODO: Crear tabla Z en SE11".

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ANÁLISIS DE LA ESPECIFICACIÓN TÉCNICA (AUTO-DETECTADO)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${resumenSecciones}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ESPECIFICACIÓN TÉCNICA COMPLETA (DOCUMENTO FUENTE)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${hayEspecificacion ? especTruncada : "[SIN ARCHIVO ADJUNTO — Generá una base estándar reutilizable para el tipo de objeto inferido del pedido]"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PEDIDO ESPECÍFICO DEL DESARROLLADOR
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${hayPedidoUsuario ? userInput : "[Sin pedido adicional — seguir 100% la especificación del documento]"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ARQUITECTURA DE INCLUDES REQUERIDA (SE38 / Modular)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Estructura el programa en los siguientes INCLUDES (reemplazá ZXXXX por el nombre detectado o un placeholder):

► ZXXXX_TOP — Declaraciones globales:
  - TYPES: Estructuras completas con todos los campos del documento.
    Si usás BKPF: incluí al menos MANDT, BUKRS, BELNR, GJAHR, BLART, BLDAT, BUDAT, WAERS.
    Si usás BSEG: incluí MANDT, BUKRS, BELNR, GJAHR, BUZEI, KOART, SAKNR, KOSTL, DMBTR, WRBTR.
    No crees estructuras de un solo campo salvo que el documento lo indique.
  - DATA / CONSTANTS relevantes al proceso.
  - TYPE-POOLS si aplica (SLIS, VRM, etc.).

► ZXXXX_SEL — Selection Screen:
  - Bloques de selección nominados (BLOCK bXX WITH FRAME TITLE tXX).
  - Si el proceso implica lectura de archivo Excel/CSV local, incluí:
    PARAMETERS: p_file TYPE rlgrap-filename.
    Con F4 usando CL_GUI_FRONTEND_SERVICES=>FILE_OPEN_DIALOG.
  - Validaciones en AT SELECTION-SCREEN.

► ZXXXX_F01 — Subrutinas (FORM ... ENDFORM):
  - Una FORM por cada proceso lógico identificado en la especificación.
  - Si hay lectura de Excel: usá TEXT_CONVERT_XLS_TO_SAP o CL_FDT_XL_SPREADSHEET.
  - Si hay JOINs entre BKPF y BSEG, usá siempre:
    INNER JOIN bseg ON bseg~bukrs = bkpf~bukrs
                   AND bseg~belnr = bkpf~belnr
                   AND bseg~gjahr = bkpf~gjahr.
  - Para tablas Z: primero SELECT de la tabla Z, luego enriquecé con JOIN a tablas estándar.

► ZXXXX_ALV (si el output es ALV):
  - Usá CL_SALV_TABLE o REUSE_ALV_GRID_DISPLAY según lo indique la especificación.
  - Definí fieldcatalog y layout basados en los campos reales del output.

${instruccionesVersion}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
GENERA AHORA el código ABAP completo respetando todo lo anterior.
Si algo no está especificado, no lo inventés: marcalo con * TODO.
`.trim();
      return promptFinal;
    };

    // ─────────────────────────────────────────────────────────────
    // BLOQUE 4: EJECUCIÓN
    // ─────────────────────────────────────────────────────────────
    try {
      let promptFinal;
      const codigoPrevioLimitado =
        isRefining && result ? result.substring(0, 10000) : null;

      // ── Si es refinamiento, NO construir prompt nuevo ──
      if (isRefining) {
        promptFinal = `MODO REFINAMIENTO — APLICAR SOLO LOS CAMBIOS INDICADOS

CÓDIGO ORIGINAL COMPLETO (CONSERVAR ÍNTEGRO):
${codigoPrevioLimitado}

CAMBIOS A APLICAR:
${refineInput}

REGLAS ABSOLUTAS:
1. Devolvé el código COMPLETO con SOLO los cambios pedidos aplicados.
2. PROHIBIDO reescribir, reorganizar ni cambiar nada que no se pidió.
3. PROHIBIDO cambiar nombres, estructuras o lógica no mencionados.
4. Al final del código agregá:
*& CAMBIOS APLICADOS:
*& - descripción de cada cambio con número de línea aproximado`;
      } else {

        // Bypass Fiori BTP/CAP — no construir prompt ABAP
        if (selectedTool?.id === 'fiori' && (wizardData?.fioriPlatform === 'BTP/CAP' || wizardData?.fioriPlatform === 'CAP')) {
          try {
            const fioriRes = await fetch(API_URL, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "X-Session-ID": instanceId,
              },
              body: JSON.stringify({
                toolId: 'fiori',
                userInput: input?.trim() || '',
                data: { ...otrosDatos, _userInput: input?.trim() || '' },
                userEmail: user?.primaryEmailAddress?.emailAddress,
              }),
            });

            const fioriData = await fioriRes.json();
            if (fioriData.capObjects) {
              setCapObjects(fioriData.capObjects);
              setResult("CAP_OBJECTS_MODE");
              showNotification("Anotaciones Fiori CAP generadas", "success");
            } else if (fioriData.result) {
              setResult(fioriData.result);
            }
          } catch (e) {
            console.error("❌ Error bypass fiori:", e.message);
          }
          setLoading(false);
          return;
        }

        promptFinal = await construirPromptTecnico();
        // Si hay archivo pero el prompt es muy corto, esperar y reconstruir
        if (fileContext?.file && promptFinal.length < 200) {
          console.warn("⚠️ Archivo no procesado todavía, esperando...");
          await new Promise((resolve) => setTimeout(resolve, 800));
          const promptReintento = await construirPromptTecnico();
          if (promptReintento.length > promptFinal.length) {
            promptFinal = promptReintento;
            console.log("✅ Reintento exitoso, largo:", promptFinal.length);
          }
        }
      }

      // Bypass Fiori BTP/CAP
      if (selectedTool?.id === 'fiori' && (wizardData?.fioriPlatform === 'BTP/CAP' || wizardData?.fioriPlatform === 'CAP')) {
        const fioriRes = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Session-ID": instanceId,
          },
          body: JSON.stringify({
            toolId: 'fiori',
            userInput: input?.trim() || '',
            data: { ...otrosDatos, _userInput: input?.trim() || '' },
            userEmail: user?.primaryEmailAddress?.emailAddress,
          }),
        });
        const fioriData = await fioriRes.json();
        if (fioriData.capObjects) {
          setCapObjects(fioriData.capObjects);
          setResult("CAP_OBJECTS_MODE");
          showNotification("Anotaciones Fiori CAP generadas", "success");
        } else if (fioriData.result) {
          setResult(fioriData.result);
        }
        setLoading(false);
        return;
      }

      const promptParaEnviar =
        promptFinal.length > 12000
          ? promptFinal.substring(0, 12000) +
          "\n\n[PROMPT TRUNCADO POR LÍMITE DEL SERVIDOR]"
          : promptFinal;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Session-ID": instanceId,
        },
        body: JSON.stringify({
          toolId: selectedTool?.id,
          userInput: promptParaEnviar,
          codigoPrevio: codigoPrevioLimitado,
          data: {
            ...otrosDatos,
            file_context: null,
            _userInput: input?.trim() || '',
          },
          instanceId,
          userEmail: user?.primaryEmailAddress?.emailAddress,
        }),
      });

      const data = await response.json();
      if (response.status === 403 && data.error === "LIMIT_REACHED") {
        showNotification("⚠️ " + data.message, "error");
        setLoading(false);
        return;
      }

      if (response.status === 409 || data.error === "SESSION_CONFLICT") {
        setSessionConflict(true);
        showNotification(
          "⚠️ Tu cuenta está activa en otro dispositivo. Cerrá esa sesión antes de continuar.",
          "error",
        );
        setLoading(false);
        return;
      }

      if (!data.result && !data.rapObjects) {
        console.error("❌ data.result está vacío. Respuesta completa:", data);
        throw new Error(
          "El servidor no devolvió resultado. Verificá el campo 'result' en la respuesta.",
        );
      }

      // Si es RAP con objetos separados
      if (selectedTool?.id === 'rap' && data.rapObjects) {
        setRapObjects(data.rapObjects);
        setResult("RAP_OBJECTS_MODE");
        showNotification("Stack RAP generado con 8 objetos", "success");
        setLoading(false);
        return;
      }

      // Si es CAP con objetos separados
      if ((selectedTool?.id === 'cap' || selectedTool?.id === 'fiori') && data.capObjects) {
        setCapObjects(data.capObjects);
        setResult("CAP_OBJECTS_MODE");
        showNotification("Stack CAP generado con 8 objetos", "success");
        setLoading(false);
        return;
      }

      let cleanResult;
      if (selectedTool?.id === 'doc') {
        cleanResult = data.result.trim();
      } else {
        cleanResult = data.result
          .replace(/```[a-z]*\n?/gi, "")
          .replace(/```/g, "")
          .trim();
      }

      setResult(getSAPHeader() + "\n\n" + cleanResult);
      showNotification("Código generado con arquitectura completa", "success");
    } catch (e) {
      console.error("❌ ERROR EN handleGenerate:", e);
      console.error("❌ Mensaje:", e?.message);
      console.error("❌ Stack:", e?.stack);
      showNotification(
        "Error: " + (e?.message || "Error desconocido"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };
  // Envía mensaje del chat a la API y agrega la respuesta al historial de conversación
  const handleChatQuery = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const token = await session.getToken();
    const userMessage = { role: "user", content: chatInput };
    setChatHistory((prev) => [...prev, userMessage]);
    setChatInput("");
    setChatLoading(true);
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Session-ID": instanceId,
        },
        body: JSON.stringify({
          toolId: "chat",
          userInput: chatInput,
          history: chatHistory.slice(-10),
          instanceId,
          userEmail: user?.primaryEmailAddress?.emailAddress,
        }),
      });
      if (response.status === 401 || response.status === 403) {
        const errorData = await response.json();
        setChatHistory((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `⚠️ ERROR: ${errorData.message || "Sesión no autorizada."}`,
          },
        ]);
        showNotification("Error de autenticación en Chat", "error");
        setChatLoading(false);
        return;
      }
      const data = await response.json();
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: data.result },
      ]);
    } catch (e) {
      console.error("Error en Chat:", e);
      showNotification("Chat no disponible en este momento", "error");
    }
    setChatLoading(false);
  };
  // Activa reconocimiento de voz en español y escribe el resultado en el input del chat
  const startVoiceRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = "es-ES";
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e) => setChatInput(e.results[0][0].transcript);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  };
  // Selecciona herramienta: resetea estado del workspace y actualiza lista de recientes
  const selectToolHandler = (tool) => {
    setActiveView("workspace");
    setSelectedTool(tool);
    setInput("");
    setResult("");
    setWizardData(initialWizardData);
    setIsSidebarOpen(false);
    if (tool) {
      setRecentTools((prev) => {
        const filtered = prev.filter((t) => t.id !== tool.id);
        return [tool, ...filtered].slice(0, 3);
      });
    }
  };
  // Modal de pago — muestra opciones de PayPal (y MP cuando esté habilitado)
  return (
    <div className="flex h-screen bg-[#0B0F1A] text-left overflow-hidden relative font-sans">
      <SignedOut>
        <LandingPage />
      </SignedOut>

      <SignedIn>
        <PayModal isPayModalOpen={isPayModalOpen}
          setIsPayModalOpen={setIsPayModalOpen}
          payLoading={payLoading}
          pagarConPayPal={pagarConPayPal}
          pagarConMP={pagarConMP}
          user={user} />
        <style
          dangerouslySetInnerHTML={{
            __html: `select { color: #090a0a !important; background-color: #ffffff !important; }
          .modern-wizard-container { display: grid; grid-template-columns: 1fr; gap: 2rem; }
          @media (min-width: 1024px) { .modern-wizard-container { grid-template-columns: 2fr 1fr; } }
          .modern-guide-banner { background: rgba(16, 185, 129, 0.05); border-left: 4px solid #10b981; }
          select:focus { outline: 2px solid #2563eb !important; border-color: transparent !important; }`,
          }}
        />

        {toast.show && (
          <div className="fixed inset-0 flex items-center justify-center z-[300] pointer-events-none">
            <div className="animate-in slide-in-from-top duration-500 pointer-events-auto">
              <div
                className={`px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-3 border uppercase text-[11px] font-black max-w-sm text-center ${toast.type === "error"
                  ? "bg-red-950 border-red-500/50 text-red-200"
                  : toast.type === "success"
                    ? "bg-emerald-950 border-emerald-500/50 text-emerald-200"
                    : "bg-white border-blue-100 text-slate-800"
                  }`}
              >
                {toast.message}
              </div>
            </div>
          </div>
        )}

        <aside
          className={`fixed lg:relative z-50 w-80 h-full bg-[#0B0F1A] 
          flex flex-col shrink-0 border-r border-white/5 transition-transform duration-300 transform 
          overflow-y-auto overflow-x-hidden ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        >
          <div className="p-6 flex-1 flex flex-col overflow-hidden text-left min-h-0">
            <div className="flex items-center justify-between mb-10 text-white">
              <div className="flex items-center gap-3">
                <div className="bg-blue-600 p-2 rounded-xl shadow-lg">
                  <Terminal size={24} />
                </div>
                <h1
                  className="text-xl font-black uppercase italic tracking-tighter cursor-pointer"
                  onClick={() => {
                    setActiveView("workspace");
                    setSelectedTool(null);
                  }}
                >
                  ORQUEST{" "}
                  <span className="text-blue-400 not-italic font-light">
                    SAP
                  </span>
                </h1>
              </div>
              <button
                className="lg:hidden"
                onClick={() => setIsSidebarOpen(false)}
              >
                <X />
              </button>
            </div>

            <nav className="space-y-4 pr-2 scrollbar-hide text-left overflow-y-auto flex-1 pb-6">
              <div className="mb-6 px-2 space-y-3 text-white">
                {/* Tarjeta de Usuario - Ahora siempre en modo PRO */}
                <div className="flex items-center justify-between p-3 rounded-2xl bg-white/5 border border-white/10 shadow-inner group transition-all hover:bg-white/10">
                  <div className="flex items-center gap-3 text-left">
                    <UserButton
                      afterSignOutUrl="/"
                      appearance={{
                        elements: {
                          // Borde verde esmeralda fijo para todos
                          avatarBox:
                            "w-9 h-9 rounded-xl border-2 border-emerald-500/50",
                        },
                      }}
                    />
                    <div className="flex flex-col text-left overflow-hidden">
                      <span className="text-[10px] font-black text-white uppercase italic truncate">
                        Consultor SAP
                      </span>
                      <span
                        className={`text-[8px] font-bold uppercase tracking-tighter ${isPro ? "text-emerald-400" : "text-red-400"}`}
                      >
                        {isPro ? "● Licencia Pro Activa" : "● Sin Plan Pro"}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-slate-500 hover:text-red-400"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              </div>

              <div>
                <UserGuideViewer
                  isOpen={isGuideOpen}
                  onClose={() => setIsGuideOpen(false)}
                />
                {isFeedbackOpen && (
                  <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center p-4 sm:p-6 backdrop-blur-sm bg-black/70">
                    <div className="bg-[#0B0F1A] border border-white/8 w-full max-w-md rounded-[32px] shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden">
                      {/* Header con gradiente */}
                      <div className="relative p-6 pb-5 border-b border-white/5">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent" />
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                              <MessageSquare size={17} className="text-white" />
                            </div>
                            <div>
                              <h3 className="text-[13px] font-black uppercase tracking-widest text-white leading-none">
                                Sugerencias
                              </h3>
                              <p className="text-[10px] text-blue-400 font-black uppercase tracking-wider mt-0.5">
                                SAP Architect
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setIsFeedbackOpen(false);
                              setFeedbackText("");
                            }}
                            className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all"
                          >
                            <X size={15} />
                          </button>
                        </div>
                      </div>

                      {/* Body */}
                      {feedbackSent ? (
                        <div className="p-10 flex flex-col items-center justify-center gap-4 text-center">
                          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                            <Check size={24} className="text-emerald-400" />
                          </div>
                          <div>
                            <p className="font-black text-white text-sm uppercase tracking-widest mb-1">
                              ¡Gracias!
                            </p>
                            <p className="text-[11px] text-white/40 leading-relaxed">
                              Tu sugerencia fue enviada al equipo de soporte.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-6 space-y-5">
                          {/* Categorías rápidas */}
                          <div>
                            <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.15em] mb-3">
                              Tipo de sugerencia
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {[
                                "Nueva herramienta",
                                "Mejora UX",
                                "Bug",
                                "Integración",
                                "Otro",
                              ].map((tag) => (
                                <button
                                  key={tag}
                                  onClick={() =>
                                    setFeedbackText((prev) =>
                                      prev.includes(tag)
                                        ? prev
                                        : `[${tag}] ${prev}`,
                                    )
                                  }
                                  className="text-[10px] font-black px-3 py-1.5 rounded-full border border-white/10 text-white/40 hover:border-blue-500/40 hover:text-blue-400 transition-all uppercase tracking-wider"
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Textarea */}
                          <div>
                            <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.15em] mb-2">
                              Tu mensaje
                            </p>
                            <textarea
                              className="w-full h-32 p-4 border 
                              border-white/8 rounded-2xl 
                              focus:border-blue-500/40 outline-none 
                              text-[13px] placeholder:text-white/20 
                              resize-none transition-all leading-relaxed"
                              style={{
                                backgroundColor: "#0B0F1A",
                                color: "#ffffff",
                              }}
                              placeholder="¿Qué nueva funcionalidad te gustaría ver?"
                              value={feedbackText}
                              onChange={(e) => setFeedbackText(e.target.value)}
                            />
                            <p className="text-[9px] text-white/15 text-right mt-1">
                              {feedbackText.length}/500
                            </p>
                          </div>

                          {/* Botón */}
                          <button
                            onClick={handleSendFeedback}
                            disabled={sendingFeedback || !feedbackText.trim()}
                            className={`w-full py-3.5 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all duration-200
                              ${sendingFeedback || !feedbackText.trim()
                                ? "bg-white/5 text-white/20 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 hover:scale-[1.01]"
                              }`}
                          >
                            {sendingFeedback ? (
                              <span className="flex items-center justify-center gap-2">
                                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Enviando…
                              </span>
                            ) : (
                              "Enviar a Soporte Técnico"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/** Opciones de suscripción PRO */}
              {isPro && (
                <div className="px-2 mb-4">
                  <div className="w-full px-3 py-2.5 rounded-xl bg-emerald-600/15 border border-emerald-500/25">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap size={13} className="text-emerald-400 shrink-0" />
                      <p className="text-[10px] font-black text-white uppercase tracking-wide leading-none">
                        ✅ Plan PRO Activo
                      </p>
                    </div>
                    {(() => {
                      const fechaVenc = user?.publicMetadata?.fechaVencimiento;
                      if (!fechaVenc) return null;
                      const dias = Math.ceil((new Date(fechaVenc) - new Date()) / (1000 * 60 * 60 * 24));
                      return (
                        <p className="text-[9px] text-white/40 leading-none mt-0.5">
                          {dias > 0 ? `Vence en ${dias} días` : "⚠️ Vencido — renovar"}
                        </p>
                      );
                    })()}
                    <button
                      onClick={async () => {
                        if (!window.confirm("¿Querés renovar tu plan PRO por otros 30 días?")) return;
                        await pagarConPayPal();
                      }}
                      className="w-full text-[9px] text-white/30 hover:text-emerald-400 transition-colors text-left mt-1"
                    >
                      Renovar suscripción →
                    </button>
                  </div>
                </div>
              )}
              {/** Opciones para usuarios sin PRO */}
              {!isPro && (
                <div className="px-2 mb-4">
                  <button
                    onClick={handleMercadoPagoSubscription}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-blue-600/15 border border-blue-500/25 hover:bg-blue-600/25 hover:border-blue-500/50 transition-all group"
                  >
                    <Zap size={13} className="text-amber-400 shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="text-[10px] font-black text-white uppercase tracking-wide leading-none">
                        ⚡ Activar PRO
                      </p>
                      <p className="text-[9px] text-white/30 leading-none mt-0.5">
                        Ilimitado · Sin restricciones
                      </p>
                    </div>
                  </button>
                </div>
              )}

              <div
                className="px-2 mb-6 cursor-pointer text-left"
                onClick={() => {
                  setActiveView("store");
                  setSelectedTool(null);
                  setIsSidebarOpen(false);
                }}
              >
                <div
                  className={`bg-white/5 border border-white/10 rounded-[24px] p-5 shadow-xl relative overflow-hidden group transition-all duration-500 ${activeView === "store" ? "border-blue-500 bg-blue-600/30" : "hover:border-blue-500/40"}`}
                >
                  <div className="flex flex-col gap-3 relative z-10 text-left text-white">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-600 p-2 rounded-xl text-white shadow-lg shadow-blue-500/30">
                        <BookOpen size={16} />
                      </div>
                      <span className="text-[9px] font-black uppercase tracking-[0.2em]">
                        Biblioteca Elite
                      </span>
                    </div>
                    <h4 className="text-[11px] font-black text-white uppercase tracking-wider leading-tight">
                      ABAP <br /> Next Gen™
                    </h4>
                    <div className="mt-2 w-full bg-white text-slate-900 py-2 rounded-xl text-[9px] font-black uppercase flex items-center justify-center gap-2 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-md">
                      Explorar Libros <ChevronRight size={10} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de filtros por categoría */}
              <div className="pt-6 border-t border-white/5 text-slate-400 text-left">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4 px-2 italic text-slate-500">
                  Categorías
                </p>
                <div className="space-y-3 px-2 text-left mb-6">
                  {["Development", "Optimization", "Reporting", "Quality"].map(
                    (filter) => (
                      <div
                        key={filter}
                        onClick={() => {
                          setActiveView("workspace");
                          setActiveFilter(filter);
                          setSelectedTool(null);
                          setIsSidebarOpen(false);
                        }}
                        className={`flex items-center gap-3 cursor-pointer transition-all 
                          ${activeFilter === filter ? "text-white font-bold" : "hover:text-white opacity-60"}`}
                      >
                        <div
                          className={`w-2.5 h-2.5 rounded-full ${filter === "Development"
                            ? "bg-blue-500"
                            : filter === "Optimization"
                              ? "bg-emerald-500"
                              : filter === "Reporting"
                                ? "bg-amber-500"
                                : "bg-purple-500"
                            }`}
                        ></div>
                        <span className="text-[10px] uppercase tracking-widest text-left">
                          {filter}
                        </span>
                      </div>
                    ),
                  )}
                </div>

                {/* Sección de Documentación y Soporte Técnico */}
                <div className="border-t border-white/5 pt-4">
                  <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.15em] mb-3 px-1">
                    Documentación
                  </p>
                  <button
                    onClick={() => {
                      setActiveView("guide");
                      setSelectedTool(null);
                      setIsSidebarOpen(false);
                    }}
                  >
                    <BookOpen size={15} className="text-blue-400 shrink-0" />
                    <div>
                      <p className="text-[11px] font-black text-white/60 uppercase tracking-wide group-hover:text-white transition-colors">
                        Guía de Usuario
                      </p>
                      <p className="text-[9px] text-white/20">
                        ES · EN · 17 herramientas
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setActiveView("foro");
                      setSelectedTool(null);
                      setIsSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5 transition-all group mt-2"
                  >
                    <MessageCircle size={15} className="text-blue-400 shrink-0" />
                    <div>
                      <p className="text-[11px] font-black text-white/60 uppercase tracking-wide group-hover:text-white transition-colors">
                        Foro Comunidad
                      </p>
                      <p className="text-[9px] text-white/20">
                        Consultores SAP · Tiempo real
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setActiveView("servicios");
                      setSelectedTool(null);
                      setIsSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5 transition-all group mt-2"
                  >
                    <Sparkles size={15} className="text-blue-400 shrink-0" />
                    <div>
                      <p className="text-[11px] font-black text-white/60 uppercase tracking-wide group-hover:text-white transition-colors">
                        Servicios
                      </p>
                      <p className="text-[9px] text-white/20">
                        Freelance · BTP · Fiori · IA
                      </p>
                    </div>
                  </button>
                </div>

                {/* Solo mostrar acceso a Panel Admin para el usuario específico */}
                {user?.primaryEmailAddress?.emailAddress === "andrescaceres37@gmail.com" && (
                  <div className="border-t border-white/5 pt-4 mt-4">
                    <button
                      onClick={() => {
                        setActiveView("admin");
                        setSelectedTool(null);
                        setIsSidebarOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-amber-500/10 transition-all group"
                    >
                      <Shield size={15} className="text-amber-400 shrink-0" />
                      <div>
                        <p className="text-[11px] font-black text-amber-400 uppercase tracking-wide">
                          Panel Admin
                        </p>
                        <p className="text-[9px] text-white/20">
                          Usuarios · Registros · Planes
                        </p>
                      </div>
                    </button>
                  </div>
                )}
                {/* Sección de Soporte Técnico */}
                <div className="mt-8 pt-8 border-t border-white/10 text-left px-2">
                  <p className="text-[12px] font-black uppercase tracking-[0.2em] mb-5 italic text-green-400/100">
                    Canal de Soporte
                  </p>
                  <button
                    onClick={() => setIsFeedbackOpen(true)}
                    className="w-full flex items-center gap-4 px-4 py-2 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-blue-500/40 hover:bg-blue-600/10 transition-all group shadow-sm"
                  >
                    <div className="bg-blue-600/20 p-2 rounded-xl text-blue-400 group-hover:text-blue-300 group-hover:scale-110 transition-all shadow-inner">
                      <MessageCircle size={18} />
                    </div>
                    <span className="text-[11px] font-black uppercase tracking-widest text-slate-200 group-hover:text-white transition-colors">
                      Enviar Sugerencia
                    </span>
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </aside>

        <main className="flex-1 flex flex-col bg-[#0B0F1A] relative overflow-hidden">
          <header className="h-16 bg-[#0B0F1A] border-b border-white/5 flex items-center justify-between px-6 lg:px-10 shrink-0">
            <div className="flex items-center gap-6 flex-1 max-w-4xl text-left text-white">
              <div className="flex items-center gap-2 lg:hidden">
                <button
                  className="p-2 text-white/50"
                  onClick={() => setIsSidebarOpen(true)}
                >
                  <Menu size={24} />
                </button>
                <button
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 ${isChatOpen ? "bg-white text-black" : "bg-blue-600 text-white"}`}
                >
                  {isChatOpen ? <X size={18} /> : <Bot size={20} />}
                </button>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-2xl border border-white/10 shadow-inner">
                  <Eye size={14} className="text-blue-500" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Usuarios Online: <span className="text-blue-400">{visits}</span>
                  </span>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-white/5 px-4 py-2 rounded-2xl border border-white/10">
                  <Activity size={12} className="text-emerald-500" />
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                    Latency:{" "}
                    <span className="text-emerald-400">{latency}ms</span>
                  </span>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 flex-1 bg-white/5 px-4 py-2 rounded-2xl border border-white/10 focus-within:border-blue-500/50 transition-all">
                <Search size={16} className="text-slate-500" />
                <input
                  type="text"
                  placeholder="Buscar herramienta..."
                  className="w-full bg-transparent outline-none text-xs font-bold text-white uppercase tracking-widest"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    if (e.target.value.trim()) {
                      setActiveFilter("Development");
                      setActiveFilter("All");
                    }
                  }}
                />
              </div>
            </div>
          </header>

          <div
            className="flex-1 overflow-y-auto p-4 lg:p-10 text-left scrollbar-hide"
            ref={scrollContainerRef}
          >
            {activeView === "admin" && isAdmin ? (
              <div className="animate-in fade-in duration-500">
                <button
                  onClick={() => setActiveView("workspace")}
                  className="flex items-center gap-2 mb-6 text-[11px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-widest transition-colors group"
                >
                  <span className="w-5 h-5 rounded-full border border-slate-600 group-hover:border-blue-400 flex items-center justify-center transition-colors">
                    <ArrowLeft size={10} />
                  </span>
                  Workspace Central
                </button>
                <AdminPanel user={user} />
              </div>
            ) : activeView === "admin" && !isAdmin ? (
              /* Opcional: Mostrar un mensaje de error si intenta forzar la entrada */
              <div className="text-red-500 font-black p-10">⚠️ ACCESO DENEGADO: SE REQUIEREN PERMISOS DE ADMINISTRADOR</div>
            ) : activeView === "servicios" ? (
              <div className="max-w-4xl mx-auto text-left">
                <button
                  onClick={() => setActiveView("workspace")}
                  className="flex items-center gap-2 mb-6 text-[11px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-widest transition-colors group"
                >
                  <span className="w-5 h-5 rounded-full border border-slate-600 group-hover:border-blue-400 flex items-center justify-center transition-colors">
                    <ArrowLeft size={10} />
                  </span>
                  Workspace Central
                </button>
                <div className="mb-10">
                  <div className="flex items-center gap-2 mb-2 text-[#0047AB]">
                    <Sparkles size={14} />
                    <span className="text-[9px] font-black uppercase tracking-[0.3em]">Servicios Profesionales</span>
                  </div>
                  <h2 className="text-3xl font-light italic uppercase tracking-tighter text-white">
                    Desarrollo <span className="text-[#0047AB] font-bold not-italic font-black">a Medida</span>
                  </h2>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em] mt-1">
                    Para empresas que buscan evolucionar su ecosistema SAP
                  </p>
                </div>
                {/* Datos de contacto */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                  {[
                    { label: "Andrés Cáceres", icon: "👤", link: null },
                    { label: "infoabapfi@gmail.com", icon: "✉️", link: "mailto:infoabapfi@gmail.com" },
                    { label: "+54 9 11 6718 4173", icon: "📱", link: "tel:+5491167184173" },
                    { label: "Argentina", icon: "🌎", link: null },
                    { label: "WhatsApp", icon: "whatsapp", link: "https://wa.me/5491167184173?text=Hola%20Andrés%2C%20me%20interesa%20conocer%20más%20sobre%20tus%20servicios%20SAP" },
                  ].map((item) => {
                    const icono = item.icon === "whatsapp" ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#22c55e' }}>
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    ) : <span>{item.icon}</span>;
                    if (item.label === "WhatsApp") {
                      return (
                        <button
                          key={item.label}
                          onClick={() => window.open("https://wa.me/5491167184173", "_blank")}
                          className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 hover:border-[#0047AB]/50 hover:bg-[#0047AB]/10 transition-all cursor-pointer"
                        >
                          {icono}
                          <span className="text-[11px] font-bold text-slate-300 truncate">{item.label}</span>
                        </button>
                      );
                    }
                    return item.link ? (
                      <div
                        key={item.label}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 hover:border-[#0047AB]/50 hover:bg-[#0047AB]/10 transition-all"
                      >
                        {icono}
                        <span className="text-[11px] font-bold text-slate-300 tracking-wide">
                          {item.label}
                        </span>
                      </div>
                    ) : (
                      <div
                        key={item.label}
                        className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2"
                      >
                        {icono}
                        <span className="text-[11px] font-bold text-slate-300 tracking-wide">
                          {item.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Cards servicios */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  {[
                    {
                      icon: <Sparkles size={18} className="text-[#0047AB]" />,
                      title: "Programas IA a Medida",
                      desc: "Desarrollo de soluciones SAP potenciadas con IA generativa, adaptadas a los procesos específicos de tu empresa. Automatización inteligente de código ABAP y reportes.",
                    },
                    {
                      icon: <Globe size={18} className="text-[#0047AB]" />,
                      title: "Fiori & BTP Freelance",
                      desc: "Desarrollo de aplicaciones Fiori Elements y freestyle sobre SAP BTP. Desde el diseño UX hasta el despliegue en la nube, con integración OData y CAP model.",
                    },
                    {
                      icon: <Code size={18} className="text-[#0047AB]" />,
                      title: "Arquitectura S/4HANA",
                      desc: "Migración y modernización de desarrollos legacy a S/4HANA. Clean ABAP, CDS Views, AMDP y optimización de rendimiento para entornos de alta demanda.",
                    },
                  ].map((s) => (
                    <div
                      key={s.title}
                      className="group bg-white/3 border border-white/8 rounded-[24px] p-6 hover:border-[#0047AB]/50 hover:bg-[#0047AB]/5 transition-all duration-300"
                    >
                      <div className="w-10 h-10 bg-[#0047AB]/15 border border-[#0047AB]/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        {s.icon}
                      </div>
                      <h3 className="text-[13px] font-black uppercase tracking-wide text-white mb-2">{s.title}</h3>
                      <p className="text-[11px] text-slate-500 leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>
                {/* CTA */}
                <div className="bg-gradient-to-r from-[#0047AB]/10 to-transparent border border-[#0047AB]/20 rounded-[28px] p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div>
                    <p className="text-[10px] font-black text-[#0047AB] uppercase tracking-widest mb-2">
                      ¿Tu empresa necesita una solución SAP?
                    </p>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">Hablemos de tu proyecto</h3>
                    <p className="text-[11px] text-slate-500 mt-1">Respuesta en menos de 24 horas · Presupuesto sin cargo</p>
                  </div>
                  <a href="mailto:infoabapfi@gmail.com"
                    target="_blank" rel="noopener noreferrer"
                    className="shrink-0 group bg-[#0047AB] text-white px-8 py-3 
                  rounded-2xl font-black text-[11px] uppercase tracking-widest 
                  flex items-center gap-3 hover:scale-105 transition-all shadow-lg 
                  shadow-[#0047AB]/20">
                    Contactar ahora
                    <ArrowLeft size={14} className="rotate-180 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            ) : activeView === "foro" ? (
              <ForoComunitario user={user} selectedTool={selectedTool} onClose={() => setActiveView("workspace")} />
            ) : activeView === "guide" ? (
              <UserGuideViewer
                isOpen={true}
                onClose={() => setActiveView("workspace")}
                inline={true}
              />
            ) : activeView === "store" ? (
              <div className="max-w-6xl mx-auto text-left">
                <div className="flex items-center justify-between mb-8 px-4 text-white">
                  <div className="text-left">
                    <div className="flex items-center gap-2 mb-1 text-blue-600">
                      <BookOpen size={14} />
                      <span className="text-[9px] font-black uppercase tracking-[0.3em]">
                        Knowledge Base Elite
                      </span>
                    </div>
                    <h2 className="text-3xl font-light italic uppercase tracking-tighter text-white">
                      Librería de{" "}
                      <span className="text-blue-600 font-bold not-italic font-black">
                        Recursos
                      </span>
                    </h2>
                  </div>
                  <button
                    onClick={() => setActiveView("workspace")}
                    className="flex items-center gap-2 text-slate-400 font-black text-[9px] uppercase hover:text-blue-600 transition-all border border-white/5 px-4 py-2 rounded-full tracking-widest"
                  >
                    <ArrowLeft size={14} /> Catálogo
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {myBooks.map((book) => (
                    <div
                      key={book.id}
                      className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[40px] shadow-xl overflow-hidden flex flex-col text-white"
                    >
                      <div className="h-72 bg-white/5 flex items-center justify-center p-6 relative">
                        <img
                          src={book.image}
                          alt={book.title}
                          className="max-h-full max-w-full object-contain shadow-2xl"
                        />
                      </div>
                      <div className="p-8 flex-1 flex flex-col text-left">
                        <h3 className="text-lg font-black text-white uppercase tracking-tight mb-4 min-h-[50px]">
                          {book.title}
                        </h3>
                        <p className="text-slate-400 text-[11px] font-medium leading-relaxed mb-8 flex-1">
                          {book.description}
                        </p>
                        <a
                          href={book.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full bg-blue-600 text-white py-4 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-blue-500 transition-all shadow-xl"
                        >
                          Adquirir
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : !selectedTool ? (
              <div className="max-w-7xl mx-auto text-left">
                <header className="mb-12 text-white">
                  <div className="flex items-center gap-2 mb-2 text-blue-600">
                    <Sparkles size={16} className="animate-pulse" />
                    <span className="text-[10px] font-black uppercase tracking-[0.3em]">
                      IA Engine Status: Active
                    </span>
                  </div>
                  <h2 className="text-4xl font-light text-white mb-1 italic uppercase tracking-tighter">
                    Workspace{" "}
                    <span className="text-blue-600 font-bold not-italic font-black">
                      Central
                    </span>
                  </h2>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em] italic">
                    "El copiloto IA para el ABAP de la nueva generación"
                  </p>
                </header>
                {activeFilter !== "All" && (
                  <button
                    onClick={() => setActiveFilter("All")}
                    className="flex items-center gap-2 mb-6 text-[11px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-widest transition-colors group"
                  >
                    <span className="w-5 h-5 rounded-full border border-slate-600 group-hover:border-blue-400 flex items-center justify-center transition-colors">
                      <ArrowLeft size={10} />
                    </span>
                    Workspace Central
                  </button>
                )}
                {activeFilter === "All" && !search.trim() ? (
                  <div className="animate-in fade-in duration-500 space-y-8">
                    {/* ── CÓMO FUNCIONA ── */}
                    <div className="w-full">
                      <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.2em] mb-3">
                        — Cómo funciona
                      </p>
                      <div className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory">
                        {[
                          {
                            step: "01",
                            icon: "🛠",
                            title: "Elegí tu herramienta",
                            desc: "17 herramientas especializadas: ALV, Programa Z, CDS, Fiori, SQL, Unit Testing, ST22 y más. Cada una con su propio wizard inteligente.",
                            color: "blue",
                          },
                          {
                            step: "02",
                            icon: "📋",
                            title: "Configurá el Wizard",
                            desc: "Completá los campos del wizard o adjuntá tu especificación funcional (PDF, DOCX, XLSX). La IA extrae automáticamente tablas, campos, módulos y lógica del documento.",
                            color: "violet",
                          },
                          {
                            step: "03",
                            icon: "⚡",
                            title: "Generá el código",
                            desc: "Claude Haiku construye el código ABAP completo en segundos, listo para copiar en SE38. Arquitectura modular, Clean ABAP y sintaxis S/4HANA por defecto.",
                            color: "emerald",
                          },
                          {
                            step: "04",
                            icon: "🔧",
                            title: "Refiná sin perder nada",
                            desc: "El Motor de Refinamiento aplica únicamente los cambios que indicás, renombrar, agregar campos, modificar SELECT, cambiar tipos conservando el código original íntegro.",
                            color: "amber",
                          },
                          {
                            step: "05",
                            icon: "📥",
                            title: "Copiá o descargá",
                            desc: "El resultado tiene syntax highlighting ABAP, botón de copia directa al portapapeles y descarga como archivo .abap listo para importar en SAP.",
                            color: "cyan",
                          },
                        ].map((item) => {
                          const colors = {
                            blue: { border: "border-blue-500/30", bg: "bg-blue-500/5", text: "text-blue-400", num: "bg-blue-500/15 border-blue-500/30" },
                            violet: { border: "border-violet-500/30", bg: "bg-violet-500/5", text: "text-violet-400", num: "bg-violet-500/15 border-violet-500/30" },
                            emerald: { border: "border-emerald-500/30", bg: "bg-emerald-500/5", text: "text-emerald-400", num: "bg-emerald-500/15 border-emerald-500/30" },
                            amber: { border: "border-amber-500/30", bg: "bg-amber-500/5", text: "text-amber-400", num: "bg-amber-500/15 border-amber-500/30" },
                            cyan: { border: "border-cyan-500/30", bg: "bg-cyan-500/5", text: "text-cyan-400", num: "bg-cyan-500/15 border-cyan-500/30" },
                          };
                          const c = colors[item.color];
                          return (
                            <div
                              key={item.step}
                              className={`shrink-0 w-52 snap-start border ${c.border} ${c.bg} rounded-[20px] p-4 flex flex-col gap-2`}
                            >
                              <div className="flex items-center gap-3">
                                <span className={`text-[11px] font-black px-2.5 py-1 rounded-lg border ${c.num} ${c.text}`}>
                                  {item.step}
                                </span>
                                <span className="text-xl">{item.icon}</span>
                              </div>
                              <h3 className={`text-[11px] font-black uppercase tracking-wide ${c.text}`}>
                                {item.title}
                              </h3>
                              <p className="text-[11px] text-white/40 leading-relaxed">
                                {item.desc}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {[
                        {
                          label: "Herramientas",
                          value: tools.length,
                          accent: "text-blue-400",
                          top: "bg-blue-500",
                          icon: <Settings2 size={16} />,
                          iconBg: "bg-blue-500/12",
                        },
                        {
                          label: "Categorías",
                          value: 4,
                          accent: "text-emerald-400",
                          top: "bg-emerald-500",
                          icon: <FolderOpen size={16} />,
                          iconBg: "bg-emerald-500/12",
                        },
                        {
                          label: "Engine IA",
                          value: "Active",
                          accent: "text-cyan-400",
                          top: "bg-cyan-500",
                          icon: <Sparkles size={16} />,
                          iconBg: "bg-cyan-500/12",
                        },
                        {
                          label: "Versión",
                          value: "v5.6.2",
                          accent: "text-amber-400",
                          top: "bg-amber-500",
                          icon: <Sparkle size={16} />,
                          iconBg: "bg-amber-500/12",
                        },
                      ].map((kpi) => (
                        <div
                          key={kpi.label}
                          className="relative bg-white/3 border border-white/7 rounded-2xl p-4 flex items-center gap-3 overflow-hidden hover:border-white/15 transition-all duration-300"
                        >
                          <div
                            className={`absolute top-0 left-0 right-0 h-[2px] ${kpi.top} rounded-t-2xl`}
                          />
                          <div
                            className={`${kpi.iconBg} p-2 rounded-xl shrink-0 ${kpi.accent}`}
                          >
                            {kpi.icon}
                          </div>
                          <div>
                            <p className={`text-xl font-black ${kpi.accent}`}>
                              {kpi.value}
                            </p>
                            <p className="text-[9px] text-white/25 uppercase tracking-widest">
                              {kpi.label}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Recientes */}
                    {recentTools.length > 0 && (
                      <div>
                        <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.2em] mb-3">
                          — Recientes
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {recentTools.map((tool) => (
                            <div
                              key={tool.id}
                              onClick={() => selectToolHandler(tool)}
                              className="group bg-white/3 border border-white/7 rounded-2xl p-4 cursor-pointer hover:border-blue-500/40 hover:bg-blue-500/5 transition-all duration-300"
                            >
                              <div className="text-blue-400 mb-3">
                                {tool.icon}
                              </div>
                              <p className="text-[11px] font-black text-white uppercase tracking-wide mb-1 group-hover:text-blue-400 transition-colors">
                                {tool.title}
                              </p>
                              <p className="text-[10px] text-white/30 line-clamp-2 leading-relaxed">
                                {tool.description}
                              </p>
                              <p className="text-[9px] text-blue-500 font-black uppercase tracking-widest mt-3 group-hover:text-blue-400 transition-colors">
                                Initialize →
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Categorías */}
                    <div>
                      <p className="text-[9px] font-black text-white/25 uppercase tracking-[0.2em] mb-3">
                        — Explorar por categoría
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          {
                            name: "Development",
                            accent: "text-blue-400",
                            iconColor: "text-blue-400",
                            bg: "bg-blue-500/7",
                            border: "border-blue-500/20",
                            hoverBorder: "hover:border-blue-500/50",
                            bar: "bg-blue-500",
                            badgeBg: "bg-blue-500/15",
                            badgeText: "text-blue-400",
                            desc: "Programas Z, Fiori, CDS Views, APIs, BAPI y arquitectura ABAP moderna.",
                            count: tools.filter((t) => t.tag === "Development")
                              .length,
                            icon: <Code size={20} />,
                          },
                          {
                            name: "Optimization",
                            accent: "text-emerald-400",
                            iconColor: "text-emerald-400",
                            bg: "bg-emerald-500/7",
                            border: "border-emerald-500/20",
                            hoverBorder: "hover:border-emerald-500/50",
                            bar: "bg-emerald-500",
                            badgeBg: "bg-emerald-500/15",
                            badgeText: "text-emerald-400",
                            desc: "SQL tuning, Field Symbols, migración S/4HANA y modernización de código legacy.",
                            count: tools.filter((t) => t.tag === "Optimization")
                              .length,
                            icon: <Zap size={20} />,
                          },
                          {
                            name: "Reporting",
                            accent: "text-amber-400",
                            iconColor: "text-amber-400",
                            bg: "bg-amber-500/7",
                            border: "border-amber-500/20",
                            hoverBorder: "hover:border-amber-500/50",
                            bar: "bg-amber-500",
                            badgeBg: "bg-amber-500/15",
                            badgeText: "text-amber-400",
                            desc: "Reportes ALV, blueprints técnicos, documentación y traducción de código.",
                            count: tools.filter((t) => t.tag === "Reporting")
                              .length,
                            icon: <FileText size={20} />,
                          },
                          {
                            name: "Quality",
                            accent: "text-purple-400",
                            iconColor: "text-purple-400",
                            bg: "bg-purple-500/7",
                            border: "border-purple-500/20",
                            hoverBorder: "hover:border-purple-500/50",
                            bar: "bg-purple-500",
                            badgeBg: "bg-purple-500/15",
                            badgeText: "text-purple-400",
                            desc: "Validación de especificaciones funcionales contra el modelo estándar de 10 secciones.",
                            count: tools.filter((t) => t.tag === "Quality")
                              .length,
                            icon: <ClipboardCheck size={20} />,
                          },
                        ].map((cat) => (
                          <div
                            key={cat.name}
                            onClick={() => {
                              setActiveFilter(cat.name);
                              setSelectedTool(null);
                            }}
                            className={`group border ${cat.border} ${cat.bg} ${cat.hoverBorder} rounded-[22px] p-6 cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:-translate-y-0.5`}
                          >
                            <div className="flex items-start justify-between mb-5">
                              <div
                                className={`${cat.iconColor} bg-white/5 p-3 rounded-2xl group-hover:scale-110 transition-transform duration-300`}
                              >
                                {cat.icon}
                              </div>
                              <span
                                className={`text-[9px] font-black px-3 py-1 rounded-full ${cat.badgeBg} ${cat.badgeText} uppercase tracking-wider`}
                              >
                                {cat.count} tool{cat.count !== 1 ? "s" : ""}
                              </span>
                            </div>
                            <p className="font-black text-sm uppercase tracking-wider text-white mb-2">
                              {cat.name}
                            </p>
                            <p className="text-[11px] text-white/35 leading-relaxed mb-5">
                              {cat.desc}
                            </p>
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 h-[2px] bg-white/5 rounded-full">
                                <div
                                  className={`h-full rounded-full ${cat.bar} transition-all duration-700`}
                                  style={{
                                    width: `${(cat.count / tools.length) * 100}%`,
                                  }}
                                />
                              </div>
                              <span
                                className={`text-[9px] font-black ${cat.accent} uppercase tracking-widest group-hover:translate-x-1 transition-transform duration-300 shrink-0`}
                              >
                                Explorar →
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── Grid de cards filtrado por categoría ── */
                  <>
                    <div className="flex items-center gap-2 mb-6">
                      <button
                        onClick={() => { setActiveFilter("All"); setSearch(""); }}
                        className="text-[11px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-widest transition-colors"
                      >
                        Workspace Central
                      </button>
                      <span className="text-slate-700 text-[11px]">›</span>
                      <span className="text-[11px] font-black text-white uppercase tracking-widest">
                        {activeFilter}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 text-white text-left">
                      {tools
                        .filter(
                          (t) =>
                            (activeFilter === "All" ||
                              t.tag === activeFilter) &&
                            t.title
                              .toLowerCase()
                              .includes(search.toLowerCase()),
                        )
                        .map((tool) => (
                          <div
                            key={tool.id}
                            onClick={() => selectToolHandler(tool)}
                            className={`group ${tool.id === "alv"
                              ? "bg-white/5 border-indigo-500/30"
                              : "bg-white/5 border-white/5"
                              } 
            backdrop-blur-md p-7 rounded-[32px] hover:border-blue-500/30 
            hover:bg-blue-500/5 cursor-pointer 
            transition-all duration-300 relative`}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveManual(tool);
                              }}
                              className="absolute top-6 right-6 z-20 flex flex-col items-center gap-1.5 group/manual"
                            >
                              <span className="text-[7px] font-black uppercase tracking-[0.2em] text-slate-500 group-hover/manual:text-blue-400 transition-colors">
                                Ver Manual
                              </span>
                              <div className="p-2 bg-blue-600/10 border border-blue-500/20 rounded-xl text-blue-500 group-hover/manual:bg-blue-600 group-hover/manual:text-white transition">
                                <Info size={14} />
                              </div>
                            </button>
                            <div className="relative z-10 text-blue-600 mb-6 group-hover:scale-110 transition-all duration-300">
                              {tool.icon}
                            </div>
                            <h3 className="text-[13px] font-black text-white uppercase italic mb-3 group-hover:text-blue-400">
                              {tool.title}
                            </h3>
                            <p className="text-[11px] text-slate-500 font-medium group-hover:text-slate-300 transition-colors">
                              {tool.description}
                            </p>
                            <div className="text-[9px] text-blue-500 font-black flex items-center gap-2 uppercase font-mono relative z-10 mt-4">
                              Initialize{" "}
                              <ArrowLeft size={12} className="rotate-180" />
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="max-w-5xl mx-auto animate-in fade-in pb-20 text-white">
                <div className="bg-white/5 backdrop-blur-3xl rounded-[24px] md:rounded-[40px] shadow-2xl border border-white/10 overflow-hidden">
                  <div className="p-8 border-b border-white/5 bg-white/5 px-10 flex justify-between items-center">
                    <button
                      onClick={() => selectToolHandler(null)}
                      className="flex items-center gap-2 text-slate-500 font-black text-[10px] uppercase hover:text-blue-400 transition-all tracking-widest"
                    >
                      <ArrowLeft size={16} /> Volver al Panel
                    </button>
                  </div>
                  <div className="p-4 md:p-12 text-left">
                    <div className="flex items-center gap-5 mb-10 text-white">
                      <div className="p-5 rounded-3xl bg-blue-600 text-white shadow-xl shadow-blue-500/20">
                        {selectedTool.icon}
                      </div>
                      <h2 className="text-3xl font-black text-white uppercase italic underline decoration-blue-500/30 decoration-4 underline-offset-4">
                        {selectedTool.title}
                      </h2>
                    </div>

                    <div className="space-y-6 text-white">
                      {selectedTool?.id === "alv" && (
                        <div className="space-y-4">
                          <AlvWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}

                      {selectedTool?.id === "new_prog" && (
                        <div className="force-input-visibility">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `.force-input-visibility input, .force-input-visibility textarea { color: white !important; background: #0f172a !important; border: 1px solid rgba(255,255,255,0.1) !important; padding: 12px !important; border-radius: 12px !important; }`,
                            }}
                          />
                          <div className="space-y-4">
                            <ProgramZWizard
                              data={wizardData}
                              setData={setWizardData}
                            />
                            <div className="bg-amber-500/10 border border-amber-500/30 p-5 rounded-[24px] flex items-center justify-between group transition-all">
                              <div className="flex items-center gap-4">
                                <div className="bg-amber-500 p-2 rounded-xl text-black shadow-lg shadow-amber-500/20">
                                  <FileText size={18} />
                                </div>
                                <div className="text-left">
                                  <h4 className="text-[10px] font-black uppercase tracking-widest text-white">
                                    Fuente de Datos Primaria
                                  </h4>
                                  <p className="text-[8px] text-amber-500 font-bold uppercase tracking-tighter opacity-70">
                                    Anular entradas manuales si se utiliza un
                                    documento
                                  </p>
                                </div>
                              </div>
                              <select
                                className="bg-black/60 border border-white/10 text-white text-[10px] font-black uppercase p-3 rounded-xl outline-none focus:border-amber-500 transition-all cursor-pointer"
                                value={
                                  wizardData.progOverrideManual
                                    ? "DOC"
                                    : "MANUAL"
                                }
                                onChange={(e) =>
                                  setWizardData((prev) => ({
                                    ...prev,
                                    progOverrideManual:
                                      e.target.value === "DOC",
                                  }))
                                }
                              >
                                <option value="MANUAL">
                                  Usar Wizard Manual
                                </option>
                                <option value="DOC">
                                  Priorizar Doc. Funcional
                                </option>
                              </select>
                            </div>
                            <div className="bg-emerald-500/5 border border-emerald-500/20 p-8 rounded-[32px] mb-6 animate-in slide-in-from-right duration-700">
                              <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-4">
                                  <div className="bg-emerald-600 p-2.5 rounded-2xl text-white shadow-lg shadow-emerald-900/20">
                                    <Database size={20} />
                                  </div>
                                  <div className="text-left">
                                    <h4 className="text-[11px] font-black uppercase tracking-widest text-white leading-none mb-1">
                                      Diccionario de Datos (SE11)
                                    </h4>
                                    <p className="text-[9px] text-emerald-500 font-bold uppercase tracking-tighter opacity-70">
                                      Define Tablas y evita campos duplicados
                                    </p>
                                  </div>
                                </div>
                                <div
                                  onClick={() =>
                                    setWizardData((prev) => ({
                                      ...prev,
                                      progGenDDIC: !prev.progGenDDIC,
                                    }))
                                  }
                                  className={`w-14 h-7 rounded-full p-1 cursor-pointer transition-all duration-300 ${wizardData.progGenDDIC ? "bg-emerald-600" : "bg-slate-800"}`}
                                >
                                  <div
                                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${wizardData.progGenDDIC ? "translate-x-7" : "translate-x-0"}`}
                                  ></div>
                                </div>
                              </div>
                              <button
                                onClick={() =>
                                  showNotification(
                                    `Estructura de ${wizardData.progTables.length} tablas sincronizada`,
                                    "success",
                                  )
                                }
                                className="w-full mt-4 py-3 bg-emerald-600/20 border border-emerald-500/40 text-emerald-400 font-black text-[9px] uppercase tracking-widest rounded-xl hover:bg-emerald-600 hover:text-white transition-all"
                              >
                                ✓ Guardar Configuración de Tablas
                              </button>
                              {wizardData.progGenDDIC && (
                                <div className="space-y-6 animate-in zoom-in-95">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-black/30 p-5 rounded-2xl border border-white/5">
                                    <div className="md:col-span-2">
                                      <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1 block">
                                        Nombre de la Tabla Z
                                      </label>
                                      <input
                                        type="text"
                                        className="w-full bg-black/40 border border-white/10 p-3 rounded-xl text-[10px] font-bold text-white uppercase outline-none focus:border-emerald-500"
                                        placeholder="Ej: ZTCLIENTES"
                                        value={tempTableName}
                                        onChange={(e) =>
                                          setTempTableName(
                                            e.target.value.toUpperCase(),
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="flex items-end">
                                      <button
                                        onClick={() => {
                                          if (!tempTableName) return;
                                          setWizardData((prev) => ({
                                            ...prev,
                                            progTables: [
                                              ...(prev.progTables || []),
                                              {
                                                tableName: tempTableName,
                                                fields: [],
                                              },
                                            ],
                                          }));
                                          setTempTableName("");
                                        }}
                                        className="w-full h-[46px] bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[9px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-emerald-900/20"
                                      >
                                        <Plus size={14} /> Crear Tabla
                                      </button>
                                    </div>
                                  </div>
                                  <div className="space-y-4">
                                    {wizardData.progTables?.map(
                                      (table, tIndex) => (
                                        <div
                                          key={tIndex}
                                          className="bg-black/40 border border-white/5 p-6 rounded-2xl space-y-4"
                                        >
                                          <div className="flex justify-between items-center border-b border-white/5 pb-3">
                                            <span className="text-[11px] font-black text-white uppercase tracking-wider">
                                              {table.tableName}
                                            </span>
                                            <button
                                              onClick={() =>
                                                setWizardData((prev) => ({
                                                  ...prev,
                                                  progTables:
                                                    prev.progTables.filter(
                                                      (_, idx) =>
                                                        idx !== tIndex,
                                                    ),
                                                }))
                                              }
                                              className="text-red-500 hover:text-red-400 text-[9px] font-black uppercase tracking-widest flex items-center gap-1"
                                            >
                                              <Trash2 size={12} /> Borrar
                                            </button>
                                          </div>
                                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                            <input
                                              type="text"
                                              className="bg-black/40 border border-white/10 p-2.5 rounded-lg text-[9px] font-bold text-white uppercase outline-none focus:border-emerald-500"
                                              placeholder="Campo (MANDT)"
                                              value={tempFieldName}
                                              onChange={(e) =>
                                                setTempFieldName(
                                                  e.target.value.toUpperCase(),
                                                )
                                              }
                                            />
                                            <input
                                              type="text"
                                              className="bg-black/40 border border-white/10 p-2.5 rounded-lg text-[9px] font-bold text-emerald-400 uppercase outline-none focus:border-emerald-500"
                                              placeholder="Tipo (CLNT)"
                                              value={tempFieldType}
                                              onChange={(e) =>
                                                setTempFieldType(
                                                  e.target.value.toUpperCase(),
                                                )
                                              }
                                            />
                                            <input
                                              type="text"
                                              className="bg-black/40 border border-white/10 p-2.5 rounded-lg text-[9px] font-bold text-white uppercase outline-none focus:border-emerald-500"
                                              placeholder="Descripción (Mandante)"
                                              value={tempFieldLabel}
                                              onChange={(e) =>
                                                setTempFieldLabel(
                                                  e.target.value,
                                                )
                                              }
                                            />
                                            <button
                                              onClick={() => {
                                                const nombreCampo =
                                                  tempFieldName
                                                    .trim()
                                                    .toUpperCase();
                                                if (
                                                  !nombreCampo ||
                                                  !tempFieldType ||
                                                  !tempFieldLabel
                                                )
                                                  return;
                                                if (
                                                  table.fields.some(
                                                    (f) =>
                                                      f.name === nombreCampo,
                                                  )
                                                ) {
                                                  showNotification(
                                                    `El campo ${nombreCampo} ya existe`,
                                                    "error",
                                                  );
                                                  return;
                                                }
                                                setWizardData((prev) => {
                                                  const nuevasTablas =
                                                    prev.progTables.map(
                                                      (t, idx) => {
                                                        if (idx === tIndex)
                                                          return {
                                                            ...t,
                                                            fields: [
                                                              ...t.fields,
                                                              {
                                                                name: nombreCampo,
                                                                type: tempFieldType.trim(),
                                                                label:
                                                                  tempFieldLabel.trim(),
                                                              },
                                                            ],
                                                          };
                                                        return t;
                                                      },
                                                    );
                                                  return {
                                                    ...prev,
                                                    progTables: nuevasTablas,
                                                  };
                                                });
                                                setTempFieldName("");
                                                setTempFieldType("");
                                                setTempFieldLabel("");
                                              }}
                                              className="bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[9px] uppercase tracking-widest rounded-lg h-[38px] transition-all active:scale-95 shadow-lg shadow-emerald-500/20"
                                            >
                                              Añadir Campo
                                            </button>
                                          </div>
                                          <div className="flex flex-wrap gap-2 pt-2">
                                            {table.fields?.map((f, fIndex) => (
                                              <div
                                                key={fIndex}
                                                className="flex items-center gap-2 bg-emerald-600/10 border border-emerald-500/30 px-3 py-1.5 rounded-lg"
                                              >
                                                <div className="flex flex-col text-left leading-tight">
                                                  <span className="text-[9px] font-black text-white">
                                                    {f.name}
                                                  </span>
                                                  <span className="text-[7px] font-bold text-emerald-400 uppercase">
                                                    {f.type}
                                                  </span>
                                                </div>
                                                <button
                                                  onClick={() =>
                                                    setWizardData((prev) => {
                                                      const nuevasTablas =
                                                        prev.progTables.map(
                                                          (t, idx) => {
                                                            if (idx === tIndex)
                                                              return {
                                                                ...t,
                                                                fields:
                                                                  t.fields.filter(
                                                                    (
                                                                      _,
                                                                      f_idx,
                                                                    ) =>
                                                                      f_idx !==
                                                                      fIndex,
                                                                  ),
                                                              };
                                                            return t;
                                                          },
                                                        );
                                                      return {
                                                        ...prev,
                                                        progTables:
                                                          nuevasTablas,
                                                      };
                                                    })
                                                  }
                                                  className="text-slate-600 hover:text-red-500 ml-1"
                                                >
                                                  <X size={12} />
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            {wizardData.progGenDDIC &&
                              wizardData.progTables?.length > 0 && (
                                <div className="mt-8 animate-in fade-in slide-in-from-top duration-700">
                                  <div className="flex items-center gap-3 mb-6 px-4">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                                      Inventario de Estructuras Z para el
                                      Programa
                                    </h4>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {wizardData.progTables.map((table, i) => (
                                      <div
                                        key={i}
                                        className="bg-black/40 border border-white/5 rounded-[24px] p-6 shadow-xl hover:border-emerald-500/20 transition-all group relative overflow-hidden"
                                      >
                                        <div className="absolute -right-4 -top-4 text-white/[0.02] rotate-12 group-hover:text-emerald-500/5 transition-colors">
                                          <Database size={80} />
                                        </div>
                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                          <div className="flex flex-col text-left">
                                            <span className="text-[12px] font-black text-white uppercase tracking-wider">
                                              {table.tableName}
                                            </span>
                                            <span className="text-[8px] text-emerald-500 font-bold uppercase tracking-widest">
                                              {table.fields?.length} Campos
                                              Definidos
                                            </span>
                                          </div>
                                          <button
                                            onClick={() =>
                                              setWizardData((prev) => ({
                                                ...prev,
                                                progTables:
                                                  wizardData.progTables.filter(
                                                    (_, idx) => idx !== i,
                                                  ),
                                              }))
                                            }
                                            className="p-2 text-slate-600 hover:text-red-500 transition-all bg-white/5 rounded-lg"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </div>
                                        <div className="space-y-2 relative z-10">
                                          {table.fields?.map((f, fi) => (
                                            <div
                                              key={fi}
                                              className="flex items-center justify-between bg-black/20 p-2 rounded-lg border border-white/5"
                                            >
                                              <div className="flex items-center gap-3">
                                                <span className="text-[9px] font-black text-slate-200 uppercase">
                                                  {f.name}
                                                </span>
                                                <span className="text-[7px] font-bold text-slate-600 uppercase italic">
                                                  ({f.label})
                                                </span>
                                              </div>
                                              <span className="text-[7px] font-black text-emerald-500/80 bg-emerald-500/5 px-2 py-0.5 rounded border border-emerald-500/10">
                                                {f.type}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                        <button
                                          onClick={() => {
                                            navigator.clipboard.writeText(
                                              JSON.stringify(table, null, 2),
                                            );
                                            showNotification(
                                              `Estructura de ${table.tableName} copiada`,
                                              "success",
                                            );
                                          }}
                                          className="w-full mt-4 py-2.5 bg-emerald-600/5 border border-emerald-500/10 text-[8px] text-emerald-500 font-black uppercase rounded-xl hover:bg-emerald-600 hover:text-white transition-all tracking-widest"
                                        >
                                          Copiar Metadatos
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}

                      {selectedTool?.id === "modern" && (
                        <div className="force-input-visibility">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `.force-input-visibility input, .force-input-visibility textarea { color: white !important; background: #0f172a !important; border: 1px solid rgba(255,255,255,0.1) !important; padding: 12px !important; border-radius: 12px !important; }`,
                            }}
                          />
                          <ModernizationWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}
                      {selectedTool?.id === "migrator" && (
                        <S4MigrationWizard
                          data={wizardData}
                          setData={setWizardData}
                        />
                      )}
                      {selectedTool?.id === "cds_composite" && (
                        <CdsCompositeWizard
                          data={wizardData}
                          setData={setWizardData} />
                      )}
                      {selectedTool?.id === "cds" && (
                        <div className="force-input-visibility">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `.force-input-visibility input, .force-input-visibility textarea { color: white !important; background: #0f172a !important; border: 1px solid rgba(255,255,255,0.1) !important; padding: 12px !important; border-radius: 12px !important; }`,
                            }}
                          />
                          <CdsWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}
                      {selectedTool?.id === "sql" && (
                        <SqlWizard data={wizardData} setData={setWizardData} />
                      )}
                      {selectedTool?.id === "fiori" && (
                        <div className="space-y-4">
                          {/* SELECTOR DE PLATAFORMA */}
                          <div className="bg-white/5 border border-white/10 rounded-[24px] p-5">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="bg-violet-600 p-1.5 rounded-lg text-white shadow-lg">
                                <Smartphone size={14} />
                              </div>
                              <div className="text-left">
                                <h4 className="text-[9px] font-black uppercase tracking-widest text-white">
                                  Plataforma Fiori
                                </h4>
                                <p className="text-[8px] text-violet-400 font-bold uppercase tracking-tighter opacity-70">
                                  El motor adaptará el tipo de anotaciones
                                  generadas
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                {
                                  id: "ABAP",
                                  label: "ABAP On-Premise",
                                  sub: "CDS Views + Gateway + metadata.xml",
                                  color: "blue",
                                },
                                {
                                  id: "CAP",
                                  label: "SAP BTP / CAP",
                                  sub: "CDS CAP + anotaciones UI + OData v4",
                                  color: "emerald",
                                },
                              ].map((v) => (
                                <button
                                  key={v.id}
                                  onClick={() =>
                                    setWizardData((prev) => ({
                                      ...prev,
                                      fioriPlatform: v.id,
                                    }))
                                  }
                                  className={[
                                    "p-4 rounded-2xl border text-left transition-all active:scale-95",
                                    wizardData.fioriPlatform === v.id
                                      ? v.color === "blue"
                                        ? "bg-blue-600/20 border-blue-500/60 shadow-lg"
                                        : "bg-emerald-600/20 border-emerald-500/60 shadow-lg"
                                      : "bg-black/20 border-white/5 hover:border-white/20",
                                  ].join(" ")}
                                >
                                  <div
                                    className={[
                                      "text-[10px] font-black uppercase tracking-wider mb-1",
                                      wizardData.fioriPlatform === v.id
                                        ? v.color === "blue"
                                          ? "text-blue-400"
                                          : "text-emerald-400"
                                        : "text-white",
                                    ].join(" ")}
                                  >
                                    {v.label}
                                  </div>
                                  <div className="text-[8px] text-slate-500 font-bold leading-tight">
                                    {v.sub}
                                  </div>
                                  {wizardData.fioriPlatform === v.id && (
                                    <div
                                      className={
                                        v.color === "blue"
                                          ? "mt-2 text-[7px] font-black uppercase text-blue-500"
                                          : "mt-2 text-[7px] font-black uppercase text-emerald-500"
                                      }
                                    >
                                      ● ACTIVO
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                          <FioriWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}
                      {selectedTool?.id === "test" && (
                        <AbapUnitWizard
                          data={wizardData}
                          setData={setWizardData}
                        />
                      )}
                      {selectedTool?.id === "json_abap" && (
                        <ApiMappingWizard
                          data={wizardData}
                          setData={setWizardData}
                        />
                      )}
                      {selectedTool?.id === "opt" && (
                        <PerformanceWizard
                          data={wizardData}
                          setData={setWizardData}
                        />
                      )}
                      {selectedTool?.id === "flowchart" && (
                        <BusinessFlowWizard
                          data={wizardData}
                          setData={setWizardData}
                        />
                      )}
                      {selectedTool?.id === "debug" && (
                        <ST22Wizard data={wizardData} setData={setWizardData} />
                      )}
                      {selectedTool?.id === "security" && (
                        <SecurityWizard
                          data={wizardData}
                          setData={setWizardData}
                        />
                      )}
                      {selectedTool?.id === "doc" && (
                        <div className="force-input-visibility">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `.force-input-visibility input, .force-input-visibility textarea { color: white !important; background: #0f172a !important; border: 1px solid rgba(255,255,255,0.1) !important; padding: 12px !important; border-radius: 12px !important; }`,
                            }}
                          />
                          <TechnicalBlueprintWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}
                      {selectedTool?.id === "translator" && (
                        <div className="force-input-visibility">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `.force-input-visibility input, .force-input-visibility textarea { color: white !important; background: #0f172a !important; border: 1px solid rgba(255,255,255,0.1) !important; padding: 12px !important; border-radius: 12px !important; }`,
                            }}
                          />
                          <TranslationWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}
                      {selectedTool?.id === "documentos" && (
                        <div className="force-input-visibility">
                          <style
                            dangerouslySetInnerHTML={{
                              __html: `.force-input-visibility input, .force-input-visibility textarea { color: white !important; background: #0f172a !important; border: 1px solid rgba(255,255,255,0.1) !important; padding: 12px !important; border-radius: 12px !important; }`,
                            }}
                          />
                          <DocumentWizard
                            data={wizardData}
                            setData={setWizardData}
                          />
                        </div>
                      )}
                      {selectedTool?.id === "specValidator" && (
                        <SpecValidatorWizard
                          data={wizardData}
                          setData={setWizardData}
                          apiUrl={API_URL}
                          getToken={() => session.getToken()}
                        />
                      )}
                      {selectedTool?.id === "rap" && (
                        <RapWizard data={wizardData} setData={setWizardData} />
                      )}
                      {selectedTool?.id === "cap" && (
                        <CapWizard data={wizardData} setData={setWizardData} />
                      )}
                      {/* SELECTOR DE VERSIÓN ABAP */}
                      <div className="bg-white/5 border border-white/10 rounded-[24px] p-5 mt-4 animate-in fade-in">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-violet-600 p-1.5 rounded-lg text-white shadow-lg">
                            <Cpu size={14} />
                          </div>
                          <div className="text-left">
                            <h4 className="text-[9px] font-black uppercase tracking-widest text-white">
                              Versión ABAP Target
                            </h4>
                            <p className="text-[8px] text-violet-400 font-bold uppercase tracking-tighter opacity-70">
                              El motor adaptará la sintaxis y arquitectura del
                              código
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {[
                            {
                              id: "S4HANA",
                              label: "S/4HANA 2021+",
                              sub: "Clean ABAP · Clases locales · Open SQL moderno",
                              color: "emerald",
                            },
                            {
                              id: "ABAP750",
                              label: "ABAP 7.50",
                              sub: "Inline declarations · VALUE # · Sin clases OBL.",
                              color: "blue",
                            },
                            {
                              id: "ABAP740",
                              label: "ABAP 7.40",
                              sub: "Compatible ECC 6.0 · FORM/PERFORM · Clásico",
                              color: "amber",
                            },
                          ].map((v) => (
                            <button
                              key={v.id}
                              onClick={() =>
                                setWizardData((prev) => ({
                                  ...prev,
                                  abapVersion: v.id,
                                }))
                              }
                              className={[
                                "p-4 rounded-2xl border text-left transition-all active:scale-95",
                                wizardData.abapVersion === v.id
                                  ? v.color === "emerald"
                                    ? "bg-emerald-600/20 border-emerald-500/60 shadow-lg"
                                    : v.color === "blue"
                                      ? "bg-blue-600/20 border-blue-500/60 shadow-lg"
                                      : "bg-amber-600/20 border-amber-500/60 shadow-lg"
                                  : "bg-black/20 border-white/5 hover:border-white/20",
                              ].join(" ")}
                            >
                              <div
                                className={[
                                  "text-[10px] font-black uppercase tracking-wider mb-1",
                                  wizardData.abapVersion === v.id
                                    ? v.color === "emerald"
                                      ? "text-emerald-400"
                                      : v.color === "blue"
                                        ? "text-blue-400"
                                        : "text-amber-400"
                                    : "text-white",
                                ].join(" ")}
                              >
                                {v.label}
                              </div>
                              <div className="text-[8px] text-slate-500 font-bold leading-tight">
                                {v.sub}
                              </div>
                              {wizardData.abapVersion === v.id && (
                                <div
                                  className={
                                    v.color === "emerald"
                                      ? "mt-2 text-[7px] font-black uppercase tracking-widest text-emerald-500"
                                      : v.color === "blue"
                                        ? "mt-2 text-[7px] font-black uppercase tracking-widest text-blue-500"
                                        : "mt-2 text-[7px] font-black uppercase tracking-widest text-amber-500"
                                  }
                                >
                                  ● ACTIVO
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* VARIANTES */}
                      <VariantesPanel
                        user={user}
                        toolId={selectedTool?.id}
                        wizardData={wizardData}
                        setWizardData={setWizardData}
                        showNotification={showNotification}
                        baseUrl={BASE}
                        userInput={input}
                        setUserInput={setInput}
                      />
                      {/* AI CONTEXT SCANNER */}
                      <div className="bg-blue-600/5 border border-white/5 rounded-[24px] p-4 relative overflow-hidden group mt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="bg-blue-600 p-1.5 rounded-lg text-white shadow-lg">
                              <Sparkles size={14} />
                            </div>
                            <div className="text-left">
                              <h4 className="text-[9px] font-black uppercase tracking-widest text-white">
                                AI Context Scanner
                              </h4>
                              <p className="text-[8px] text-blue-400 font-bold uppercase tracking-tighter opacity-70">
                                Enriquecer con archivos (PDF, DOCX, XLSX, JSON,
                                TXT)
                              </p>
                            </div>
                          </div>
                          <div className="flex-1 max-w-[400px] ml-10">
                            {wizardData.fileStatus === "pending" ? (
                              <label className="flex items-center justify-center w-full h-12 border border-dashed border-white/10 rounded-xl cursor-pointer hover:border-blue-500/40 hover:bg-blue-500/5 transition-all">
                                <div className="flex items-center gap-3">
                                  <Download
                                    size={14}
                                    className="text-slate-500"
                                  />
                                  <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
                                    Adjuntar Contexto
                                  </p>
                                </div>
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={handleFileUpload}
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.json"
                                />
                              </label>
                            ) : (
                              <div className="flex items-center justify-between bg-black/40 border border-emerald-500/30 px-4 py-2 rounded-xl animate-in slide-in-from-right">
                                <div className="flex items-center gap-3">
                                  <CheckCircle
                                    size={14}
                                    className="text-emerald-400"
                                  />
                                  <span className="text-[9px] font-black text-white uppercase truncate max-w-[150px]">
                                    {wizardData.fileContext?.name}
                                  </span>
                                </div>
                                <button
                                  onClick={removeUploadedFile}
                                  className="p-1 text-slate-500 hover:text-red-400 transition-all"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-6 text-left mt-10 text-white">
                      <div className="flex justify-between items-end mb-3">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block italic">
                          Requerimiento IA{" "}
                          {isPro && (
                            <span className="ml-3 text-blue-500 animate-pulse text-[8px] tracking-[0.2em] font-black">
                              (Ctrl + Enter para ejecutar)
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleClearAll}
                            className="flex items-center gap-2 text-[9px] font-black uppercase text-slate-500 hover:text-red-500 transition-all"
                          >
                            <Trash2 size={12} /> Limpiar Todo
                          </button>
                          <button
                            onClick={() => {
                              if (input) {
                                navigator.clipboard.writeText(input);
                              }
                            }}
                            className="flex items-center gap-2 text-[9px] font-black uppercase text-slate-500 hover:text-blue-400 transition-all"
                          >
                            <Copy size={12} /> Copiar
                          </button>
                        </div>
                      </div>
                      <textarea
                        className="w-full h-40 p-6 bg-black/40 border border-white/10 rounded-[24px] focus:border-blue-500/50 outline-none text-sm font-mono text-white placeholder:text-slate-800"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Describa su requerimiento aquí..."
                      />
                    </div>
                    {!isPro && (
                      <div className="px-2 mb-4">
                        <button
                          onClick={handleMercadoPagoSubscription}
                          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-blue-600/15 border border-blue-500/25 hover:bg-blue-600/25 hover:border-blue-500/50 transition-all group"
                        >
                          <Zap size={13} className="text-amber-400 shrink-0" />
                          <div className="text-left min-w-0">
                            <p className="text-[10px] font-black text-white uppercase tracking-wide leading-none">
                              ⚡ Activar PRO
                            </p>
                            <p className="text-[9px] text-white/30 leading-none mt-0.5">
                              Ilimitado · Sin restricciones
                            </p>
                          </div>
                        </button>
                      </div>
                    )}
                    {isPro || dailyCount < 1 ? (
                      <button
                        onClick={() => handleGenerate()}
                        disabled={loading || sessionConflict}
                        className={`w-full py-5 rounded-[20px] font-black text-white text-xs 
                        uppercase tracking-[0.3em] transition-all 
                        shadow-xl flex items-center justify-center ${sessionConflict
                            ? "bg-red-900/40 border border-red-500/50 cursor-not-allowed"
                            : loading
                              ? "bg-white/5 animate-pulse text-slate-500"
                              : "bg-blue-600 hover:bg-blue-500 active:scale-[0.98]"
                          }`}
                      >
                        {sessionConflict
                          ? "⚠️ SESIÓN ACTIVA EN OTRO DISPOSITIVO"
                          : loading
                            ? "PROCESANDO..."
                            : selectedTool.btn}
                      </button>
                    ) : (
                      <div className="relative flex justify-center py-10 animate-in zoom-in-95 duration-700 text-center">
                        <div className="absolute -right-4 top-10 w-32 h-48 bg-indigo-500/10 backdrop-blur-2xl border border-white/5 rounded-[40px] rotate-12 -z-10 shadow-xl"></div>
                        <div className="relative w-full max-w-lg backdrop-blur-3xl bg-[#020617] border border-cyan-500/20 rounded-[48px] shadow-[0_40px_80px_rgba(0,0,0,0.5)] p-12 overflow-hidden text-center">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-500/5 rotate-45 rounded-3xl blur-2xl"></div>
                          <div className="relative z-10 text-center">
                            <div className="bg-amber-500/10 border border-amber-500/30 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner">
                              <Lock size={28} className="text-amber-500" />
                            </div>
                            <h3 className="text-3xl font-black italic uppercase tracking-tighter text-white mb-2 leading-none">
                              <span className="text-white">ORQUEST</span>
                              <span className="text-blue-500">PRO</span>
                            </h3>
                            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.5em] mb-10">
                              Neural Access Required
                            </p>
                            <div className="space-y-4 mb-12">
                              <p className="text-slate-400 font-bold uppercase tracking-[0.3em] text-[10px]">
                                MÓDULO DE GENERACIÓN BLOQUEADO.
                              </p>
                              <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px]">
                                SE REQUIERE UNA LICENCIA ACTIVA PARA USAR LA IA.
                              </p>
                            </div>
                            <button
                              onClick={handleMercadoPagoSubscription}
                              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-[10px] font-black uppercase bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-lg animate-pulse hover:scale-105 active:scale-95 transition-all"
                            >
                              <Zap size={14} className="fill-white" />{" "}
                              Desbloquear Acceso PRO
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedTool?.id === 'rap' && rapObjects && (
                      <RapExplorer rapObjects={rapObjects} />
                    )}
                    {(selectedTool?.id === 'cap' || selectedTool?.id === 'fiori') && capObjects && (
                      <CapExplorer capObjects={capObjects} />
                    )}
                    {result && result !== "RAP_OBJECTS_MODE" && result !== "CAP_OBJECTS_MODE" && !loading && (
                      <div className="mt-6 rounded-[16px] overflow-hidden border border-white/10 shadow-2xl animate-in slide-in-from-bottom duration-700 relative text-white w-full">
                        <div className="bg-white/5 px-4 md:px-8 py-3 flex justify-between items-center border-b border-white/5 text-white">
                          <span className="text-[9px] md:text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] md:tracking-[0.3em] italic">
                            Technical Output
                          </span>
                          <div className="flex items-center gap-2 md:gap-6">
                            <button
                              onClick={handleToggleFavorite}
                              className={`text-[9px] font-black uppercase flex items-center gap-1 md:gap-2 transition-all ${isFavorite ? "text-amber-400" : "text-slate-400 hover:text-white"}`}
                            >
                              <Star
                                size={12}
                                className={isFavorite ? "fill-amber-400" : ""}
                              />
                              <span className="hidden md:inline">{isFavorite ? "En Favoritos" : "Favoritos"}</span>
                            </button>
                            <button
                              onClick={handleDownload}
                              className="text-[9px] text-slate-400 font-black uppercase flex items-center gap-1 md:gap-2 hover:text-emerald-400 transition-all"
                            >
                              <Download size={12} />
                              <span className="hidden md:inline">Descargar</span>
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(result);
                                showNotification("Código copiado", "success");
                                setCopied(true);
                                setTimeout(() => setCopied(false), 2000);
                              }}
                              className="text-[9px] text-blue-400 font-black uppercase flex items-center gap-1 md:gap-2"
                            >
                              {copied ? (
                                <>
                                  <Check size={12} />
                                  <span className="hidden md:inline">Copiado</span>
                                </>
                              ) : (
                                <>
                                  <Copy size={12} />
                                  <span className="hidden md:inline">Copiar</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        {result && (() => {
                          // Diagrama Mermaid puro
                          if (result.trim().startsWith('graph') || result.trim().startsWith('flowchart') || result.trim().startsWith('sequenceDiagram') || result.trim().startsWith('classDiagram')) {
                            return (
                              <div className="w-full overflow-x-auto p-8 bg-black min-h-[400px] flex items-center justify-center">
                                <div
                                  key={result}
                                  className="mermaid"
                                  ref={async (el) => {
                                    if (el) {
                                      try {
                                        let mermaidCode = result.substring(result.indexOf('graph') !== -1 ? result.indexOf('graph') : 0);
                                        mermaidCode = mermaidCode.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').replace(/[\u{2600}-\u{26FF}]/gu, '').replace(/[\u{2700}-\u{27BF}]/gu, '');
                                        el.removeAttribute('data-processed');
                                        el.innerHTML = DOMPurify.sanitize(mermaidCode);
                                        await mermaid.run({ nodes: [el] });
                                      } catch (err) {
                                        el.innerHTML = DOMPurify.sanitize(`<pre style="color:white;font-size:11px;overflow:auto">${result}</pre>`);
                                      }
                                    }
                                  }}
                                />
                              </div>
                            );
                          }

                          // Markdown con bloques Mermaid embebidos
                          if (result.includes('```mermaid')) {
                            const parts = result.split(/(```mermaid[\s\S]*?```)/g);
                            return (
                              <div className="w-full bg-black space-y-4">
                                {parts.map((part, idx) => {
                                  if (part.startsWith('```mermaid')) {
                                    const code = part.replace(/```mermaid\n?/, '').replace(/```$/, '').trim();
                                    return (
                                      <div key={idx} className="w-full overflow-x-auto bg-slate-900/50 rounded-2xl p-6 flex items-center justify-center border border-white/5">
                                        <div
                                          className="mermaid"
                                          ref={async (el) => {
                                            if (el) {
                                              try {
                                                let clean = code.replace(/[\u1F300-\u1F9FF]/gu, '').replace(/[\u2600-\u26FF]/gu, '').replace(/[\u2700-\u27BF]/gu, '');
                                                el.removeAttribute('data-processed');
                                                el.innerHTML = DOMPurify.sanitize(clean);
                                                await mermaid.run({ nodes: [el] });
                                              } catch (err) {
                                                el.innerHTML = DOMPurify.sanitize(`<pre style="color:#94a3b8;font-size:11px;overflow:auto">${code}</pre>`);
                                              }
                                            }
                                          }}
                                        />
                                      </div>
                                    );
                                  }
                                  return part.trim() ? (
                                    <SyntaxHighlighter
                                      key={idx}
                                      language="markdown"
                                      style={vscDarkPlus}
                                      customStyle={{
                                        padding: "2rem",
                                        fontSize: "13px",
                                        margin: 0,
                                        background: "#000000",
                                        borderRadius: "16px",
                                      }}
                                    >
                                      {part}
                                    </SyntaxHighlighter>
                                  ) : null;
                                })}
                              </div>
                            );
                          }

                          // Código ABAP normal
                          return (
                            <SyntaxHighlighter
                              language="abap"
                              style={vscDarkPlus}
                              customStyle={{
                                padding: window.innerWidth < 768 ? "1rem" : "3rem",
                                fontSize: window.innerWidth < 768 ? "11px" : "14px",
                                margin: 0,
                                background: "#000000",
                                overflowX: "auto",
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                              }}
                              wrapLines={true}
                              wrapLongLines={true}
                            >
                              {result}
                            </SyntaxHighlighter>
                          );
                        })()}
                        <div className="mt-8 p-8 bg-blue-600/5 border border-blue-500/20 rounded-[32px] animate-in fade-in slide-in-from-bottom duration-1000">
                          <div className="flex items-center gap-3 mb-5">
                            <div className="bg-blue-600 p-2 rounded-xl text-white shadow-lg shadow-blue-900/20">
                              <RefreshCw
                                size={18}
                                className={loading ? "animate-spin" : ""}
                              />
                            </div>
                            <div className="text-left">
                              <h4 className="text-[11px] font-black uppercase tracking-widest text-white">
                                Refinar este código
                              </h4>
                              <p className="text-[9px] text-blue-400 font-bold uppercase tracking-tighter opacity-70">
                                Indica cambios o mejoras sobre el resultado
                                anterior
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-4">
                            <div className="w-full bg-black/40 border border-white/10 rounded-2xl overflow-hidden shadow-inner">
                              {/* REFINEMENT SUGGESTION INPUT */}
                              {(() => {
                                const id = selectedTool?.id;
                                const campos = {
                                  alv: [
                                    { key: "renombrar", label: "Renombrar", placeholder: "ej: REPORT zcasa 'por' REPORT zcasa_2" },
                                    { key: "tipo", label: "Cambiar tipo", placeholder: "ej: ty_pedido 'por' tys_pedidos" },
                                    { key: "campo", label: "Agregar campo", placeholder: "ej: agregar matkl TYPE ekpo-matkl en la estructura" },
                                    { key: "where", label: "Modificar SELECT", placeholder: "ej: agregar WHERE ekko~bedat >= p_fecha" },
                                    { key: "columna", label: "Columna ALV", placeholder: "ej: agregar columna MATKL con texto 'Grupo Material'" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                  new_prog: [
                                    { key: "renombrar", label: "Renombrar", placeholder: "ej: ZPROGRAM 'por' ZPROGRAM_V2" },
                                    { key: "include", label: "Modificar Include", placeholder: "ej: agregar FORM calcular_total en _F01" },
                                    { key: "campo", label: "Agregar campo", placeholder: "ej: agregar campo kunnr TYPE kna1-kunnr en estructura" },
                                    { key: "where", label: "Modificar SELECT", placeholder: "ej: agregar filtro WHERE vbak~audat >= p_fecha" },
                                    { key: "param", label: "Agregar parámetro", placeholder: "ej: agregar PARAMETERS p_gjahr TYPE gjahr" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                  cds: [
                                    { key: "renombrar", label: "Renombrar", placeholder: "ej: ZC_PEDIDOS 'por' ZC_PEDIDOS_V2" },
                                    { key: "campo", label: "Agregar campo", placeholder: "ej: agregar campo lifnr con asociación a LFA1" },
                                    { key: "anotacion", label: "Agregar anotación", placeholder: "ej: agregar @UI.lineItem para campo netwr" },
                                    { key: "join", label: "Agregar JOIN", placeholder: "ej: agregar LEFT OUTER JOIN a MARA por matnr" },
                                    { key: "filtro", label: "Agregar filtro", placeholder: "ej: agregar WHERE bukrs = $parameters.p_bukrs" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                  cds_composite: [
                                    { key: "renombrar", label: "Renombrar", placeholder: "ej: ZC_SalesOrderFull → ZC_SalesOrderV2" },
                                    { key: "association", label: "Agregar association", placeholder: "ej: agregar _Material apuntando a ZI_MaterialMaster" },
                                    { key: "campo", label: "Agregar campo", placeholder: "ej: agregar campo OrderType en la proyección" },
                                    { key: "filtro", label: "Modificar filtro", placeholder: "ej: agregar WHERE SalesOrg = '1000'" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                  fiori: [
                                    { key: "renombrar", label: "Renombrar", placeholder: "ej: ZC_PEDIDOS 'por' ZC_FACTURAS" },
                                    { key: "campo", label: "Agregar campo UI", placeholder: "ej: agregar campo netwr en LineItem con label 'Monto'" },
                                    { key: "filtro", label: "Agregar filtro", placeholder: "ej: agregar SelectionField para bukrs" },
                                    { key: "accion", label: "Agregar acción", placeholder: "ej: agregar botón de acción 'Aprobar' en header" },
                                    { key: "anotacion", label: "Modificar anotación", placeholder: "ej: cambiar @UI.headerInfo.title a ebeln" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                  sql: [
                                    { key: "tabla", label: "Cambiar tabla", placeholder: "ej: reemplazar EKKO por VBAK" },
                                    { key: "campo", label: "Agregar campo", placeholder: "ej: agregar campo audat al SELECT" },
                                    { key: "where", label: "Modificar WHERE", placeholder: "ej: agregar filtro AND vbak~vkorg = @p_vkorg" },
                                    { key: "join", label: "Agregar JOIN", placeholder: "ej: agregar INNER JOIN KNA1 por kunnr" },
                                    { key: "orden", label: "Ordenar por", placeholder: "ej: agregar ORDER BY audat DESCENDING" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                  opt: [
                                    { key: "campo", label: "Campo a optimizar", placeholder: "ej: reemplazar LOOP AT con FIELD-SYMBOLS" },
                                    { key: "indice", label: "Agregar índice", placeholder: "ej: usar BINARY SEARCH en tabla gt_datos" },
                                    { key: "select", label: "Optimizar SELECT", placeholder: "ej: reemplazar SELECT * por lista de campos" },
                                    { key: "modulo", label: "Modularizar", placeholder: "ej: extraer lógica de cálculo a método estático" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra optimización" },
                                  ],
                                  modern: [
                                    { key: "sintaxis", label: "Modernizar", placeholder: "ej: reemplazar CONCATENATE por string template" },
                                    { key: "inline", label: "Inline declaration", placeholder: "ej: reemplazar DATA: lv_var con DATA(lv_var) =" },
                                    { key: "value", label: "Usar VALUE #", placeholder: "ej: inicializar estructura con VALUE #( campo = val )" },
                                    { key: "clase", label: "Convertir a clase", placeholder: "ej: convertir FORM calcular en método de clase local" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modernización" },
                                  ],
                                  test: [
                                    { key: "metodo", label: "Agregar método", placeholder: "ej: agregar test method test_calcular_total" },
                                    { key: "assert", label: "Agregar assert", placeholder: "ej: agregar cl_abap_unit_assert=>assertEquals" },
                                    { key: "fixture", label: "Modificar fixture", placeholder: "ej: agregar dato de prueba en setup( )" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación al test" },
                                  ],
                                  security: [
                                    { key: "objeto", label: "Objeto auth", placeholder: "ej: agregar AUTHORITY-CHECK OBJECT 'M_BEST_BSA'" },
                                    { key: "actividad", label: "Actividad", placeholder: "ej: verificar actividad '03' (Display)" },
                                    { key: "log", label: "Agregar log", placeholder: "ej: registrar acceso fallido en tabla ZLOG_AUTH" },
                                    { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                  ],
                                };

                                const arr = campos[id] || [
                                  { key: "renombrar", label: "Renombrar", placeholder: "ej: nombre_actual 'por' nombre_nuevo" },
                                  { key: "campo", label: "Agregar campo", placeholder: "ej: descripción del campo a agregar" },
                                  { key: "logica", label: "Modificar lógica", placeholder: "ej: descripción del cambio lógico" },
                                  { key: "otro", label: "Otro cambio", placeholder: "ej: cualquier otra modificación" },
                                ];

                                return arr.map((campo, idx) => (
                                  <div
                                    key={campo.key}
                                    className={`flex items-center gap-3 px-4 py-2.5 ${idx !== arr.length - 1 ? "border-b border-white/5" : ""}`}
                                  >
                                    <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest shrink-0 w-28">
                                      {campo.label}:
                                    </span>
                                    <input
                                      type="text"
                                      placeholder={campo.placeholder}
                                      className="flex-1 bg-transparent outline-none text-[11px] font-mono text-white placeholder:text-slate-700"
                                      onChange={(e) => {
                                        const inputs = arr.map((c) => {
                                          const el = document.getElementById(`refine-${c.key}`);
                                          return el?.value ? `• ${c.label}: ${el.value}` : "";
                                        }).filter(Boolean).join("\n");
                                        setRefineInput(inputs);
                                      }}
                                      id={`refine-${campo.key}`}
                                    />
                                  </div>
                                ));
                              })()}
                            </div>
                            <button
                              onClick={() => handleGenerate(true)}
                              disabled={loading}
                              className={`w-full py-4 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${loading ? "bg-white/5 text-slate-500" : "bg-blue-600 hover:bg-blue-500 text-white shadow-xl shadow-blue-900/20 active:scale-95"}`}
                            >
                              {loading ? "PROCESANDO CAMBIOS..." : "APLICAR AJUSTES AL CÓDIGO"}
                              <ArrowLeft size={14} className="rotate-180" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showScrollTop && (
              <button
                onClick={scrollToTop}
                className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 
                bg-white/10 backdrop-blur-md border 
                border-white/20 p-4 rounded-full 
                text-white shadow-2xl hover:bg-blue-600 
                transition-all animate-bounce"
              >
                <ArrowUp size={20} />
              </button>
            )}
          </div>

          <footer className="p-8 border-t border-white/5 bg-[#0B0F1A] shrink-0 px-12 relative overflow-hidden flex flex-col sm:flex-row justify-between items-center gap-6">
            <div className="flex-1 flex justify-start items-center gap-4 z-10 text-white">
              <div className="bg-blue-600/10 p-2 rounded-xl border border-blue-500/20 text-white">
                <Terminal size={18} className="text-blue-400 shadow-sm" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white">
                ORQUEST{" "}
                <span className="text-blue-400 italic">SAP ARCHITECT</span>
              </span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center z-10 text-center text-white">
              <div className="flex items-center gap-4 mb-1">
                <div className="h-[1px] w-10 bg-slate-700"></div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  DISEÑADO POR{" "}
                  <span className="text-white font-black italic ml-1">
                    ANDRÉS CÁCERES
                  </span>
                </span>
                <div className="h-[1px] w-10 bg-slate-700"></div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></div>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono">
                  SUITE EMPRESARIAL{" "}
                  <span className="text-blue-500 font-bold">V5.6.2</span>
                </span>
              </div>
            </div>
            <div className="flex-1 flex justify-end items-center gap-6 z-10 text-white">
              <div className="px-4 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-500/5 shadow-inner">
                <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest">
                  Link v9.0 Pro
                </span>
              </div>
            </div>
          </footer>
        </main>

        {/* Botón flotante chat — solo desktop */}
        <div className="fixed bottom-6 right-6 z-[400] flex-col items-end text-left hidden lg:flex">
          {!isChatOpen && (
            <div className="bg-[#0B0F1A] border-2 border-blue-500/20 px-6 py-3 rounded-full shadow-2xl mb-4 text-left backdrop-blur-xl">
              <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest whitespace-nowrap px-2">
                ¿Dudas técnicas? Consulta aquí 💬
              </p>
            </div>
          )}
          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            className={`w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 ${isChatOpen ? "bg-white text-black rotate-90 shadow-white/20" : "bg-blue-600 text-white shadow-blue-500/30"}`}
          >
            {isChatOpen ? <X size={28} /> : <Bot size={32} />}
          </button>
        </div>

        {/* Panel del chat — mobile y desktop */}
        <aside
          className={`fixed z-[600]
    bottom-0 left-0 right-0 w-full h-[85dvh] rounded-t-[28px]
    lg:bottom-24 lg:right-6 lg:left-auto lg:w-[420px] lg:h-[600px] lg:max-h-[80dvh] lg:rounded-[28px]
    bg-[#0B0F1A] backdrop-blur-3xl
    shadow-[0_20px_80px_rgba(0,0,0,0.5)] border border-white/10 flex flex-col
    overflow-hidden transition-all duration-500 origin-bottom transform
    ${isChatOpen ? "scale-100 opacity-100" : "scale-0 opacity-0 pointer-events-none"}`}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/5 bg-white/3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600/20 p-2 rounded-xl text-cyan-400">
                <Cpu size={20} />
              </div>
              <div>
                <h3 className="text-[11px] font-black uppercase tracking-widest text-white leading-none mb-0.5">
                  Architect Support
                </h3>
                <p className="text-[9px] text-blue-500 font-black uppercase italic tracking-wider">
                  ● Neural Link Active
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (window.confirm("¿Limpiar historial?")) setChatHistory([]);
                }}
                className="p-2 text-slate-600 hover:text-red-400 transition-all hover:bg-red-500/10 rounded-xl"
                title="Limpiar Chat"
              >
                <Trash2 size={16} />
              </button>
              {/* Botón cerrar — visible en mobile */}
              <button
                onClick={() => setIsChatOpen(false)}
                className="p-2 text-slate-600 hover:text-white transition-all lg:hidden"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-hide text-left">
            {chatHistory.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
                <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-cyan-400">
                  <Cpu size={22} />
                </div>
                <p className="text-[12px] font-black text-white uppercase tracking-wide">
                  ¿En qué te ayudo?
                </p>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Consultame sobre ABAP, SAP, arquitectura de sistemas o cualquier duda técnica.
                </p>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {["¿Qué es un BAPI?", "Diferencia SALV vs ALV OO", "¿Cómo funciona CDS?"].map((q) => (
                    <button
                      key={q}
                      onClick={() => setChatInput(q)}
                      className="text-[10px] px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:border-blue-500/40 hover:text-blue-400 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {chatHistory.map((msg, i) => (
              <div
                key={i}
                ref={i === chatHistory.length - 1 ? chatEndRef : null}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[82%] px-4 py-3 rounded-2xl text-[12px] leading-relaxed border
            ${msg.role === "user"
                      ? "bg-blue-600 border-blue-500 text-white rounded-br-sm"
                      : "bg-white/5 border-white/8 text-slate-300 rounded-bl-sm"
                    }`}
                >
                  {msg.content.split("\n").map((line, j) => {
                    if (line.startsWith("### "))
                      return <p key={j} className="font-black text-white text-[11px] uppercase tracking-wide mt-2 mb-1">{line.replace("### ", "")}</p>;
                    if (line.startsWith("## "))
                      return <p key={j} className="font-black text-blue-400 text-[12px] mt-2 mb-1">{line.replace("## ", "")}</p>;
                    if (line.startsWith("- ") || line.startsWith("* "))
                      return <div key={j} className="flex gap-1.5 my-0.5"><span className="text-blue-400 shrink-0">›</span><span>{line.replace(/^[-*] /, "")}</span></div>;
                    if (line.trim() === "") return <div key={j} className="h-1.5" />;
                    const parts = line.split(/\*\*(.*?)\*\*/g);
                    return <p key={j} className="my-0.5">{parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-white font-black">{part}</strong> : part)}</p>;
                  })}
                </div>
              </div>
            ))}

            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/8 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-4 border-t border-white/5 bg-white/3">
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-3 py-2 focus-within:border-blue-500/50 transition-all">
              <button
                onClick={startVoiceRecognition}
                className={`p-2 rounded-xl transition-all shrink-0 ${isListening ? "bg-red-500 text-white animate-pulse" : "text-slate-500 hover:text-blue-400"}`}
              >
                <Mic size={16} />
              </button>
              <input
                type="text"
                className="flex-1 bg-transparent outline-none text-[13px] text-white font-medium placeholder:text-slate-600"
                placeholder="Consultar duda técnica..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleChatQuery()}
              />
              <button
                onClick={handleChatQuery}
                disabled={!chatInput.trim() || chatLoading}
                className={`p-2 rounded-xl transition-all shrink-0 ${chatInput.trim() && !chatLoading ? "bg-blue-600 text-white hover:bg-blue-700" : "text-slate-700"}`}
              >
                <ArrowLeft size={16} className="rotate-180" />
              </button>
            </div>
          </div>
        </aside>

        {/* ManualModal y botón TOP — fuera del div hidden */}
        <ManualModal
          tool={activeManual}
          isOpen={!!activeManual}
          onClose={() => setActiveManual(null)}
          onStart={() => {
            selectToolHandler(activeManual);
            setActiveManual(null);
          }}
        />
        <button
          onClick={scrollToTop}
          className="fixed bottom-1 left-1/2 -translate-x-1/2 z-[500] bg-blue-600/20 backdrop-blur-md border border-blue-500/40 p-4 rounded-2xl text-blue-400 shadow-[0_0_30px_rgba(37,99,235,0.3)] hover:bg-blue-600 hover:text-white transition-all animate-in slide-in-from-bottom duration-500 group lg:block hidden"
        >
          <div className="flex flex-col items-center gap-1">
            <ArrowUp size={20} className="group-hover:-translate-y-1 transition-transform" />
            <span className="text-[8px] font-black uppercase tracking-widest">Top</span>
          </div>
        </button>
      </SignedIn >
    </div >
  );
}

export default App;
